import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import { format, addDays, addMonths } from 'date-fns';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FlightSearch } from './components/FlightSearch';
import { FlightResults } from './components/FlightResults';
import { MultiCityResults } from './components/multicity/MultiCityResults';
import { BookingFlow } from './components/BookingFlow';
import { FareCalendarGrid } from './components/FareCalendarGrid';
import { UmrahPackages } from './components/umrah/packages/UmrahPackages';
import { HoldBookingSuccess } from './components/HoldBookingSuccess';
import PayBalance from './components/umrah/packages/PayBalance';
import PayFlightBalance from './components/booking/PayFlightBalance';
import { FlightPaymentSuccess } from './components/FlightPaymentSuccess';
import { DuffelBookingFlow } from './components/DuffelBookingFlow';
import { HolidayBookingSuccess } from './components/HolidayBookingSuccess';
import { WhatsAppButton } from './components/WhatsAppButton';
import { AuthCallback } from './components/AuthCallback';
import { LoginPage } from './components/LoginPage';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { AuthPromptModal } from './components/AuthPromptModal';
import { ContactForm } from './components/ContactForm';
import { ManageBooking } from './components/ManageBooking';
import { CustomerDashboard } from './components/CustomerDashboard';
import { AdminDashboard } from './components/admin';
import AgentBooking from './components/AgentBooking';
import { UmrahAnnouncementModal } from './components/UmrahAnnouncementModal';
import UmrahQuoteDepositSuccessPage from './pages/UmrahQuoteDepositSuccessPage';
import FlightTrackerPage from './pages/FlightTrackerPage';
import CareersPage from './pages/CareersPage';
import { AIChatWidget } from './components/AIChatWidget';
import { useAIAgent } from './contexts/AIAgentContext';
import { useCurrency } from './contexts/CurrencyContext';
import { Money } from './contexts/CurrencyContext';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Calendar } from './components/ui/calendar';
import { HolidayCalendar } from './components/ui/holiday-calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { 
  BadgePoundSterling, 
  Zap, 
  Headphones, 
  CalendarClock, 
  Shield, 
  Gift,
  Star,
  ArrowRight,
  CheckCircle2,
  Users,
  Globe,
  Award,
  Heart,
  MapPin,
  Phone,
  Mail,
  Clock,
  Plane,
  Edit3,
  X,
  CalendarIcon,
  Hotel,
  CreditCard,
  ChevronDown,
  Plus,
  Minus,
  Building2
} from 'lucide-react';
import { popularDestinations, testimonials, features } from './mock';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// sessionStorage is capped (~5MB). A 49-cell flexible search can carry 500+
// flights, so write defensively: try the full payload, fall back to a slim
// version (metadata only) and never let a quota error crash the app.
const safeSessionSet = (key, value, slim) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    try {
      sessionStorage.setItem(key, JSON.stringify(slim ? slim() : {}));
    } catch (_) {
      sessionStorage.removeItem(key);
    }
  }
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  
  // App state - moved before conditional returns
  const [searchResults, setSearchResults] = useState([]);
  const [pinnedFlights, setPinnedFlights] = useState([]);  // Preferred airline direct flights
  const [pinnedAirlineName, setPinnedAirlineName] = useState(null);  // e.g., "Emirates"
  const [pinnedAirlineCode, setPinnedAirlineCode] = useState(null);  // e.g., "EK"
  const [multiCityNote, setMultiCityNote] = useState(null);  // why BFM returned no multi-city itineraries
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [isFlexibleSearch, setIsFlexibleSearch] = useState(false);
  const [savedSearchData, setSavedSearchData] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [duffelFlight, setDuffelFlight] = useState(null);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [showFareGrid, setShowFareGrid] = useState(false);
  const [showUmrahBuilder, setShowUmrahBuilder] = useState(false);
  const [preselectedUmrahDeal, setPreselectedUmrahDeal] = useState(null); // For Umrah deal cards
  const [showManageBooking, setShowManageBooking] = useState(false);
  const [showCustomerDashboard, setShowCustomerDashboard] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(180); // Default fallback height
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [pendingFlight, setPendingFlight] = useState(null);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [revalidationError, setRevalidationError] = useState(null);
  const [skipItineraryReview, setSkipItineraryReview] = useState(false);
  
  // Holiday Package Builder State
  const [showHolidayPackageBuilder, setShowHolidayPackageBuilder] = useState(false);
  const [selectedHolidayDestination, setSelectedHolidayDestination] = useState(null);
  const [holidayPackageData, setHolidayPackageData] = useState(null);
  const [isLoadingHolidayPackages, setIsLoadingHolidayPackages] = useState(false);
  // Edit mode for package builder - 'flights' | 'hotel' | null
  const [holidayPackageEditMode, setHolidayPackageEditMode] = useState(null);
  const [holidayDepartureCity, setHolidayDepartureCity] = useState('London Heathrow (LHR)');
  // Customer details modal state
  const [showHolidayCustomerDetails, setShowHolidayCustomerDetails] = useState(false);
  const [holidayContactFirstName, setHolidayContactFirstName] = useState('');
  const [holidayContactLastName, setHolidayContactLastName] = useState('');
  const [holidayContactEmail, setHolidayContactEmail] = useState('');
  const [holidayContactPhone, setHolidayContactPhone] = useState('');
  const [holidayPassengers, setHolidayPassengers] = useState([]);
  const [isProcessingHolidayBooking, setIsProcessingHolidayBooking] = useState(false);
  // Explore All Destinations page state
  const [showExploreDestinations, setShowExploreDestinations] = useState(false);
  const [exploreSearchQuery, setExploreSearchQuery] = useState('');
  const [destinationSearchOpen, setDestinationSearchOpen] = useState(false);
  const [destinationSearchText, setDestinationSearchText] = useState('');
  const [selectedDestinationCity, setSelectedDestinationCity] = useState('');
  // Date selection state
  const [holidayCheckInDate, setHolidayCheckInDate] = useState(null);
  const [holidayCheckOutDate, setHolidayCheckOutDate] = useState(null);
  const [holidayDatePickerOpen, setHolidayDatePickerOpen] = useState(false);
  // Guests state (Umrah Builder pattern)
  const [holidayAdults, setHolidayAdults] = useState(2);
  const [holidayChildren, setHolidayChildren] = useState(0);
  const [holidayChildAges, setHolidayChildAges] = useState([]);
  const [holidayInfants, setHolidayInfants] = useState(0);
  const [holidayInfantAges, setHolidayInfantAges] = useState([]);
  const [holidayRooms, setHolidayRooms] = useState(1);
  const [holidayGuestsOpen, setHolidayGuestsOpen] = useState(false);
  
  // Global Currency State - lifted to CurrencyContext for real-time conversion
  const { currency: globalCurrency, setCurrency: setGlobalCurrency } = useCurrency();
  const [globalDepartureAirport, setGlobalDepartureAirport] = useState(null);

  // AI Agent context bridging — switches the assistant persona based on view
  const { switchContext: switchAIContext, updateState: updateAIState } = useAIAgent();

  // Keep the AI agent's persona in sync with the current funnel stage
  useEffect(() => {
    let next = 'homepage';
    if (showBooking) next = 'checkout';
    else if (showUmrahBuilder) next = 'umrah';
    else if (showResults) next = 'homepage';
    switchAIContext(next);
  }, [showResults, showBooking, showUmrahBuilder, switchAIContext]);

  // Share the visitor's high-level state with the AI agent so it can ground answers
  useEffect(() => {
    updateAIState({
      currency: globalCurrency,
      departure: globalDepartureAirport,
    });
  }, [globalCurrency, globalDepartureAirport, updateAIState]);
  
  // World Destinations Data for Search
  const worldDestinations = [
    // Popular Destinations
    { city: 'Dubai', country: 'UAE', airport: 'DXB', region: 'Popular' },
    { city: 'Paris', country: 'France', airport: 'CDG', region: 'Popular' },
    { city: 'New York', country: 'USA', airport: 'JFK', region: 'Popular' },
    { city: 'Tokyo', country: 'Japan', airport: 'NRT', region: 'Popular' },
    { city: 'Barcelona', country: 'Spain', airport: 'BCN', region: 'Popular' },
    { city: 'Bali', country: 'Indonesia', airport: 'DPS', region: 'Popular' },
    { city: 'London', country: 'UK', airport: 'LHR', region: 'Popular' },
    { city: 'Singapore', country: 'Singapore', airport: 'SIN', region: 'Popular' },
    { city: 'Bangkok', country: 'Thailand', airport: 'BKK', region: 'Popular' },
    { city: 'Maldives', country: 'Maldives', airport: 'MLE', region: 'Popular' },
    // Europe - Western
    { city: 'Amsterdam', country: 'Netherlands', airport: 'AMS', region: 'Europe' },
    { city: 'Rome', country: 'Italy', airport: 'FCO', region: 'Europe' },
    { city: 'Venice', country: 'Italy', airport: 'VCE', region: 'Europe' },
    { city: 'Milan', country: 'Italy', airport: 'MXP', region: 'Europe' },
    { city: 'Florence', country: 'Italy', airport: 'FLR', region: 'Europe' },
    { city: 'Naples', country: 'Italy', airport: 'NAP', region: 'Europe' },
    { city: 'Bologna', country: 'Italy', airport: 'BLQ', region: 'Europe' },
    { city: 'Pisa', country: 'Italy', airport: 'PSA', region: 'Europe' },
    { city: 'Madrid', country: 'Spain', airport: 'MAD', region: 'Europe' },
    { city: 'Seville', country: 'Spain', airport: 'SVQ', region: 'Europe' },
    { city: 'Valencia', country: 'Spain', airport: 'VLC', region: 'Europe' },
    { city: 'Malaga', country: 'Spain', airport: 'AGP', region: 'Europe' },
    { city: 'Bilbao', country: 'Spain', airport: 'BIO', region: 'Europe' },
    { city: 'Lisbon', country: 'Portugal', airport: 'LIS', region: 'Europe' },
    { city: 'Porto', country: 'Portugal', airport: 'OPO', region: 'Europe' },
    { city: 'Berlin', country: 'Germany', airport: 'BER', region: 'Europe' },
    { city: 'Munich', country: 'Germany', airport: 'MUC', region: 'Europe' },
    { city: 'Frankfurt', country: 'Germany', airport: 'FRA', region: 'Europe' },
    { city: 'Hamburg', country: 'Germany', airport: 'HAM', region: 'Europe' },
    { city: 'Dusseldorf', country: 'Germany', airport: 'DUS', region: 'Europe' },
    { city: 'Cologne', country: 'Germany', airport: 'CGN', region: 'Europe' },
    { city: 'Vienna', country: 'Austria', airport: 'VIE', region: 'Europe' },
    { city: 'Salzburg', country: 'Austria', airport: 'SZG', region: 'Europe' },
    { city: 'Innsbruck', country: 'Austria', airport: 'INN', region: 'Europe' },
    { city: 'Zurich', country: 'Switzerland', airport: 'ZRH', region: 'Europe' },
    { city: 'Geneva', country: 'Switzerland', airport: 'GVA', region: 'Europe' },
    { city: 'Basel', country: 'Switzerland', airport: 'BSL', region: 'Europe' },
    { city: 'Brussels', country: 'Belgium', airport: 'BRU', region: 'Europe' },
    { city: 'Nice', country: 'France', airport: 'NCE', region: 'Europe' },
    { city: 'Lyon', country: 'France', airport: 'LYS', region: 'Europe' },
    { city: 'Marseille', country: 'France', airport: 'MRS', region: 'Europe' },
    { city: 'Bordeaux', country: 'France', airport: 'BOD', region: 'Europe' },
    { city: 'Toulouse', country: 'France', airport: 'TLS', region: 'Europe' },
    { city: 'Monaco', country: 'Monaco', airport: 'MCM', region: 'Europe' },
    // Europe - Northern
    { city: 'Copenhagen', country: 'Denmark', airport: 'CPH', region: 'Europe' },
    { city: 'Stockholm', country: 'Sweden', airport: 'ARN', region: 'Europe' },
    { city: 'Gothenburg', country: 'Sweden', airport: 'GOT', region: 'Europe' },
    { city: 'Oslo', country: 'Norway', airport: 'OSL', region: 'Europe' },
    { city: 'Bergen', country: 'Norway', airport: 'BGO', region: 'Europe' },
    { city: 'Helsinki', country: 'Finland', airport: 'HEL', region: 'Europe' },
    { city: 'Reykjavik', country: 'Iceland', airport: 'KEF', region: 'Europe' },
    { city: 'Dublin', country: 'Ireland', airport: 'DUB', region: 'Europe' },
    { city: 'Edinburgh', country: 'Scotland', airport: 'EDI', region: 'Europe' },
    { city: 'Glasgow', country: 'Scotland', airport: 'GLA', region: 'Europe' },
    { city: 'Manchester', country: 'UK', airport: 'MAN', region: 'Europe' },
    { city: 'Birmingham', country: 'UK', airport: 'BHX', region: 'Europe' },
    // Europe - Eastern
    { city: 'Prague', country: 'Czech Republic', airport: 'PRG', region: 'Europe' },
    { city: 'Budapest', country: 'Hungary', airport: 'BUD', region: 'Europe' },
    { city: 'Warsaw', country: 'Poland', airport: 'WAW', region: 'Europe' },
    { city: 'Krakow', country: 'Poland', airport: 'KRK', region: 'Europe' },
    { city: 'Gdansk', country: 'Poland', airport: 'GDN', region: 'Europe' },
    { city: 'Bucharest', country: 'Romania', airport: 'OTP', region: 'Europe' },
    { city: 'Sofia', country: 'Bulgaria', airport: 'SOF', region: 'Europe' },
    { city: 'Belgrade', country: 'Serbia', airport: 'BEG', region: 'Europe' },
    { city: 'Zagreb', country: 'Croatia', airport: 'ZAG', region: 'Europe' },
    { city: 'Ljubljana', country: 'Slovenia', airport: 'LJU', region: 'Europe' },
    { city: 'Bratislava', country: 'Slovakia', airport: 'BTS', region: 'Europe' },
    { city: 'Tallinn', country: 'Estonia', airport: 'TLL', region: 'Europe' },
    { city: 'Riga', country: 'Latvia', airport: 'RIX', region: 'Europe' },
    { city: 'Vilnius', country: 'Lithuania', airport: 'VNO', region: 'Europe' },
    { city: 'Moscow', country: 'Russia', airport: 'SVO', region: 'Europe' },
    { city: 'St Petersburg', country: 'Russia', airport: 'LED', region: 'Europe' },
    { city: 'Kyiv', country: 'Ukraine', airport: 'KBP', region: 'Europe' },
    // Europe - Mediterranean
    { city: 'Athens', country: 'Greece', airport: 'ATH', region: 'Europe' },
    { city: 'Santorini', country: 'Greece', airport: 'JTR', region: 'Europe' },
    { city: 'Mykonos', country: 'Greece', airport: 'JMK', region: 'Europe' },
    { city: 'Thessaloniki', country: 'Greece', airport: 'SKG', region: 'Europe' },
    { city: 'Dubrovnik', country: 'Croatia', airport: 'DBV', region: 'Europe' },
    { city: 'Split', country: 'Croatia', airport: 'SPU', region: 'Europe' },
    // Americas - North
    { city: 'Los Angeles', country: 'USA', airport: 'LAX', region: 'Americas' },
    { city: 'Miami', country: 'USA', airport: 'MIA', region: 'Americas' },
    { city: 'Las Vegas', country: 'USA', airport: 'LAS', region: 'Americas' },
    { city: 'San Francisco', country: 'USA', airport: 'SFO', region: 'Americas' },
    { city: 'Orlando', country: 'USA', airport: 'MCO', region: 'Americas' },
    { city: 'Chicago', country: 'USA', airport: 'ORD', region: 'Americas' },
    { city: 'Boston', country: 'USA', airport: 'BOS', region: 'Americas' },
    { city: 'Washington DC', country: 'USA', airport: 'IAD', region: 'Americas' },
    { city: 'Seattle', country: 'USA', airport: 'SEA', region: 'Americas' },
    { city: 'San Diego', country: 'USA', airport: 'SAN', region: 'Americas' },
    { city: 'Denver', country: 'USA', airport: 'DEN', region: 'Americas' },
    { city: 'Atlanta', country: 'USA', airport: 'ATL', region: 'Americas' },
    { city: 'Dallas', country: 'USA', airport: 'DFW', region: 'Americas' },
    { city: 'Houston', country: 'USA', airport: 'IAH', region: 'Americas' },
    { city: 'Phoenix', country: 'USA', airport: 'PHX', region: 'Americas' },
    { city: 'New Orleans', country: 'USA', airport: 'MSY', region: 'Americas' },
    { city: 'Nashville', country: 'USA', airport: 'BNA', region: 'Americas' },
    { city: 'Hawaii Honolulu', country: 'USA', airport: 'HNL', region: 'Americas' },
    { city: 'Maui', country: 'USA', airport: 'OGG', region: 'Americas' },
    { city: 'Toronto', country: 'Canada', airport: 'YYZ', region: 'Americas' },
    { city: 'Vancouver', country: 'Canada', airport: 'YVR', region: 'Americas' },
    { city: 'Montreal', country: 'Canada', airport: 'YUL', region: 'Americas' },
    { city: 'Calgary', country: 'Canada', airport: 'YYC', region: 'Americas' },
    { city: 'Ottawa', country: 'Canada', airport: 'YOW', region: 'Americas' },
    // Americas - Caribbean
    { city: 'Cancun', country: 'Mexico', airport: 'CUN', region: 'Americas' },
    { city: 'Mexico City', country: 'Mexico', airport: 'MEX', region: 'Americas' },
    { city: 'Playa del Carmen', country: 'Mexico', airport: 'CUN', region: 'Americas' },
    { city: 'Los Cabos', country: 'Mexico', airport: 'SJD', region: 'Americas' },
    { city: 'Puerto Vallarta', country: 'Mexico', airport: 'PVR', region: 'Americas' },
    { city: 'Punta Cana', country: 'Dominican Republic', airport: 'PUJ', region: 'Americas' },
    { city: 'Santo Domingo', country: 'Dominican Republic', airport: 'SDQ', region: 'Americas' },
    { city: 'Montego Bay', country: 'Jamaica', airport: 'MBJ', region: 'Americas' },
    { city: 'Kingston', country: 'Jamaica', airport: 'KIN', region: 'Americas' },
    { city: 'Barbados', country: 'Barbados', airport: 'BGI', region: 'Americas' },
    { city: 'Nassau', country: 'Bahamas', airport: 'NAS', region: 'Americas' },
    { city: 'Aruba', country: 'Aruba', airport: 'AUA', region: 'Americas' },
    { city: 'Curacao', country: 'Curacao', airport: 'CUR', region: 'Americas' },
    { city: 'Havana', country: 'Cuba', airport: 'HAV', region: 'Americas' },
    { city: 'St Lucia', country: 'St Lucia', airport: 'UVF', region: 'Americas' },
    { city: 'Antigua', country: 'Antigua', airport: 'ANU', region: 'Americas' },
    { city: 'St Maarten', country: 'St Maarten', airport: 'SXM', region: 'Americas' },
    { city: 'Turks and Caicos', country: 'Turks and Caicos', airport: 'PLS', region: 'Americas' },
    { city: 'Cayman Islands', country: 'Cayman Islands', airport: 'GCM', region: 'Americas' },
    { city: 'Puerto Rico', country: 'Puerto Rico', airport: 'SJU', region: 'Americas' },
    { city: 'US Virgin Islands', country: 'US Virgin Islands', airport: 'STT', region: 'Americas' },
    { city: 'Trinidad', country: 'Trinidad and Tobago', airport: 'POS', region: 'Americas' },
    // Americas - South
    { city: 'Rio de Janeiro', country: 'Brazil', airport: 'GIG', region: 'Americas' },
    { city: 'Sao Paulo', country: 'Brazil', airport: 'GRU', region: 'Americas' },
    { city: 'Buenos Aires', country: 'Argentina', airport: 'EZE', region: 'Americas' },
    { city: 'Lima', country: 'Peru', airport: 'LIM', region: 'Americas' },
    { city: 'Cusco', country: 'Peru', airport: 'CUZ', region: 'Americas' },
    { city: 'Bogota', country: 'Colombia', airport: 'BOG', region: 'Americas' },
    { city: 'Cartagena', country: 'Colombia', airport: 'CTG', region: 'Americas' },
    { city: 'Medellin', country: 'Colombia', airport: 'MDE', region: 'Americas' },
    { city: 'Santiago', country: 'Chile', airport: 'SCL', region: 'Americas' },
    { city: 'Quito', country: 'Ecuador', airport: 'UIO', region: 'Americas' },
    { city: 'Galapagos', country: 'Ecuador', airport: 'GPS', region: 'Americas' },
    { city: 'San Jose', country: 'Costa Rica', airport: 'SJO', region: 'Americas' },
    { city: 'Panama City', country: 'Panama', airport: 'PTY', region: 'Americas' },
    // Asia - East
    { city: 'Hong Kong', country: 'Hong Kong', airport: 'HKG', region: 'Asia' },
    { city: 'Seoul', country: 'South Korea', airport: 'ICN', region: 'Asia' },
    { city: 'Busan', country: 'South Korea', airport: 'PUS', region: 'Asia' },
    { city: 'Osaka', country: 'Japan', airport: 'KIX', region: 'Asia' },
    { city: 'Kyoto', country: 'Japan', airport: 'KIX', region: 'Asia' },
    { city: 'Fukuoka', country: 'Japan', airport: 'FUK', region: 'Asia' },
    { city: 'Sapporo', country: 'Japan', airport: 'CTS', region: 'Asia' },
    { city: 'Okinawa', country: 'Japan', airport: 'OKA', region: 'Asia' },
    { city: 'Beijing', country: 'China', airport: 'PEK', region: 'Asia' },
    { city: 'Shanghai', country: 'China', airport: 'PVG', region: 'Asia' },
    { city: 'Guangzhou', country: 'China', airport: 'CAN', region: 'Asia' },
    { city: 'Shenzhen', country: 'China', airport: 'SZX', region: 'Asia' },
    { city: 'Chengdu', country: 'China', airport: 'CTU', region: 'Asia' },
    { city: 'Xian', country: 'China', airport: 'XIY', region: 'Asia' },
    { city: 'Taipei', country: 'Taiwan', airport: 'TPE', region: 'Asia' },
    { city: 'Macau', country: 'Macau', airport: 'MFM', region: 'Asia' },
    // Asia - Southeast
    { city: 'Phuket', country: 'Thailand', airport: 'HKT', region: 'Asia' },
    { city: 'Koh Samui', country: 'Thailand', airport: 'USM', region: 'Asia' },
    { city: 'Chiang Mai', country: 'Thailand', airport: 'CNX', region: 'Asia' },
    { city: 'Krabi', country: 'Thailand', airport: 'KBV', region: 'Asia' },
    { city: 'Pattaya', country: 'Thailand', airport: 'UTP', region: 'Asia' },
    { city: 'Kuala Lumpur', country: 'Malaysia', airport: 'KUL', region: 'Asia' },
    { city: 'Langkawi', country: 'Malaysia', airport: 'LGK', region: 'Asia' },
    { city: 'Penang', country: 'Malaysia', airport: 'PEN', region: 'Asia' },
    { city: 'Kota Kinabalu', country: 'Malaysia', airport: 'BKI', region: 'Asia' },
    { city: 'Ho Chi Minh City', country: 'Vietnam', airport: 'SGN', region: 'Asia' },
    { city: 'Hanoi', country: 'Vietnam', airport: 'HAN', region: 'Asia' },
    { city: 'Da Nang', country: 'Vietnam', airport: 'DAD', region: 'Asia' },
    { city: 'Nha Trang', country: 'Vietnam', airport: 'CXR', region: 'Asia' },
    { city: 'Phu Quoc', country: 'Vietnam', airport: 'PQC', region: 'Asia' },
    { city: 'Jakarta', country: 'Indonesia', airport: 'CGK', region: 'Asia' },
    { city: 'Lombok', country: 'Indonesia', airport: 'LOP', region: 'Asia' },
    { city: 'Yogyakarta', country: 'Indonesia', airport: 'JOG', region: 'Asia' },
    { city: 'Manila', country: 'Philippines', airport: 'MNL', region: 'Asia' },
    { city: 'Cebu', country: 'Philippines', airport: 'CEB', region: 'Asia' },
    { city: 'Boracay', country: 'Philippines', airport: 'KLO', region: 'Asia' },
    { city: 'Palawan', country: 'Philippines', airport: 'PPS', region: 'Asia' },
    { city: 'Phnom Penh', country: 'Cambodia', airport: 'PNH', region: 'Asia' },
    { city: 'Siem Reap', country: 'Cambodia', airport: 'REP', region: 'Asia' },
    { city: 'Vientiane', country: 'Laos', airport: 'VTE', region: 'Asia' },
    { city: 'Luang Prabang', country: 'Laos', airport: 'LPQ', region: 'Asia' },
    { city: 'Yangon', country: 'Myanmar', airport: 'RGN', region: 'Asia' },
    // Asia - South
    { city: 'Colombo', country: 'Sri Lanka', airport: 'CMB', region: 'Asia' },
    { city: 'Goa', country: 'India', airport: 'GOI', region: 'Asia' },
    { city: 'Delhi', country: 'India', airport: 'DEL', region: 'Asia' },
    { city: 'Mumbai', country: 'India', airport: 'BOM', region: 'Asia' },
    { city: 'Bangalore', country: 'India', airport: 'BLR', region: 'Asia' },
    { city: 'Chennai', country: 'India', airport: 'MAA', region: 'Asia' },
    { city: 'Kolkata', country: 'India', airport: 'CCU', region: 'Asia' },
    { city: 'Jaipur', country: 'India', airport: 'JAI', region: 'Asia' },
    { city: 'Agra', country: 'India', airport: 'AGR', region: 'Asia' },
    { city: 'Kerala', country: 'India', airport: 'COK', region: 'Asia' },
    { city: 'Hyderabad', country: 'India', airport: 'HYD', region: 'Asia' },
    { city: 'Kathmandu', country: 'Nepal', airport: 'KTM', region: 'Asia' },
    { city: 'Dhaka', country: 'Bangladesh', airport: 'DAC', region: 'Asia' },
    { city: 'Karachi', country: 'Pakistan', airport: 'KHI', region: 'Asia' },
    { city: 'Lahore', country: 'Pakistan', airport: 'LHE', region: 'Asia' },
    { city: 'Islamabad', country: 'Pakistan', airport: 'ISB', region: 'Asia' },
    // Asia - Pacific
    { city: 'Sydney', country: 'Australia', airport: 'SYD', region: 'Asia' },
    { city: 'Melbourne', country: 'Australia', airport: 'MEL', region: 'Asia' },
    { city: 'Brisbane', country: 'Australia', airport: 'BNE', region: 'Asia' },
    { city: 'Perth', country: 'Australia', airport: 'PER', region: 'Asia' },
    { city: 'Gold Coast', country: 'Australia', airport: 'OOL', region: 'Asia' },
    { city: 'Cairns', country: 'Australia', airport: 'CNS', region: 'Asia' },
    { city: 'Adelaide', country: 'Australia', airport: 'ADL', region: 'Asia' },
    { city: 'Auckland', country: 'New Zealand', airport: 'AKL', region: 'Asia' },
    { city: 'Queenstown', country: 'New Zealand', airport: 'ZQN', region: 'Asia' },
    { city: 'Wellington', country: 'New Zealand', airport: 'WLG', region: 'Asia' },
    { city: 'Christchurch', country: 'New Zealand', airport: 'CHC', region: 'Asia' },
    { city: 'Fiji', country: 'Fiji', airport: 'NAN', region: 'Asia' },
    { city: 'Tahiti', country: 'French Polynesia', airport: 'PPT', region: 'Asia' },
    { city: 'Bora Bora', country: 'French Polynesia', airport: 'BOB', region: 'Asia' },
    // Middle East
    { city: 'Abu Dhabi', country: 'UAE', airport: 'AUH', region: 'Middle East' },
    { city: 'Sharjah', country: 'UAE', airport: 'SHJ', region: 'Middle East' },
    { city: 'Ras Al Khaimah', country: 'UAE', airport: 'RKT', region: 'Middle East' },
    { city: 'Doha', country: 'Qatar', airport: 'DOH', region: 'Middle East' },
    { city: 'Muscat', country: 'Oman', airport: 'MCT', region: 'Middle East' },
    { city: 'Salalah', country: 'Oman', airport: 'SLL', region: 'Middle East' },
    { city: 'Manama', country: 'Bahrain', airport: 'BAH', region: 'Middle East' },
    { city: 'Kuwait City', country: 'Kuwait', airport: 'KWI', region: 'Middle East' },
    { city: 'Riyadh', country: 'Saudi Arabia', airport: 'RUH', region: 'Middle East' },
    { city: 'Jeddah', country: 'Saudi Arabia', airport: 'JED', region: 'Middle East' },
    { city: 'Medina', country: 'Saudi Arabia', airport: 'MED', region: 'Middle East' },
    { city: 'Amman', country: 'Jordan', airport: 'AMM', region: 'Middle East' },
    { city: 'Aqaba', country: 'Jordan', airport: 'AQJ', region: 'Middle East' },
    { city: 'Petra', country: 'Jordan', airport: 'AMM', region: 'Middle East' },
    { city: 'Tel Aviv', country: 'Israel', airport: 'TLV', region: 'Middle East' },
    { city: 'Jerusalem', country: 'Israel', airport: 'TLV', region: 'Middle East' },
    { city: 'Beirut', country: 'Lebanon', airport: 'BEY', region: 'Middle East' },
    { city: 'Istanbul', country: 'Turkey', airport: 'IST', region: 'Middle East' },
    { city: 'Antalya', country: 'Turkey', airport: 'AYT', region: 'Middle East' },
    { city: 'Bodrum', country: 'Turkey', airport: 'BJV', region: 'Middle East' },
    { city: 'Cappadocia', country: 'Turkey', airport: 'NAV', region: 'Middle East' },
    { city: 'Izmir', country: 'Turkey', airport: 'ADB', region: 'Middle East' },
    { city: 'Fethiye', country: 'Turkey', airport: 'DLM', region: 'Middle East' },
    // Africa - North
    { city: 'Marrakech', country: 'Morocco', airport: 'RAK', region: 'Africa' },
    { city: 'Casablanca', country: 'Morocco', airport: 'CMN', region: 'Africa' },
    { city: 'Fez', country: 'Morocco', airport: 'FEZ', region: 'Africa' },
    { city: 'Agadir', country: 'Morocco', airport: 'AGA', region: 'Africa' },
    { city: 'Tangier', country: 'Morocco', airport: 'TNG', region: 'Africa' },
    { city: 'Cairo', country: 'Egypt', airport: 'CAI', region: 'Africa' },
    { city: 'Sharm El Sheikh', country: 'Egypt', airport: 'SSH', region: 'Africa' },
    { city: 'Hurghada', country: 'Egypt', airport: 'HRG', region: 'Africa' },
    { city: 'Luxor', country: 'Egypt', airport: 'LXR', region: 'Africa' },
    { city: 'Aswan', country: 'Egypt', airport: 'ASW', region: 'Africa' },
    { city: 'Alexandria', country: 'Egypt', airport: 'HBE', region: 'Africa' },
    { city: 'Tunis', country: 'Tunisia', airport: 'TUN', region: 'Africa' },
    { city: 'Djerba', country: 'Tunisia', airport: 'DJE', region: 'Africa' },
    { city: 'Algiers', country: 'Algeria', airport: 'ALG', region: 'Africa' },
    // Africa - Sub-Saharan
    { city: 'Cape Town', country: 'South Africa', airport: 'CPT', region: 'Africa' },
    { city: 'Johannesburg', country: 'South Africa', airport: 'JNB', region: 'Africa' },
    { city: 'Durban', country: 'South Africa', airport: 'DUR', region: 'Africa' },
    { city: 'Kruger Park', country: 'South Africa', airport: 'MQP', region: 'Africa' },
    { city: 'Victoria Falls', country: 'Zimbabwe', airport: 'VFA', region: 'Africa' },
    { city: 'Mauritius', country: 'Mauritius', airport: 'MRU', region: 'Africa' },
    { city: 'Seychelles', country: 'Seychelles', airport: 'SEZ', region: 'Africa' },
    { city: 'Zanzibar', country: 'Tanzania', airport: 'ZNZ', region: 'Africa' },
    { city: 'Dar es Salaam', country: 'Tanzania', airport: 'DAR', region: 'Africa' },
    { city: 'Kilimanjaro', country: 'Tanzania', airport: 'JRO', region: 'Africa' },
    { city: 'Nairobi', country: 'Kenya', airport: 'NBO', region: 'Africa' },
    { city: 'Mombasa', country: 'Kenya', airport: 'MBA', region: 'Africa' },
    { city: 'Maasai Mara', country: 'Kenya', airport: 'NBO', region: 'Africa' },
    { city: 'Kigali', country: 'Rwanda', airport: 'KGL', region: 'Africa' },
    { city: 'Addis Ababa', country: 'Ethiopia', airport: 'ADD', region: 'Africa' },
    { city: 'Lagos', country: 'Nigeria', airport: 'LOS', region: 'Africa' },
    { city: 'Accra', country: 'Ghana', airport: 'ACC', region: 'Africa' },
    { city: 'Dakar', country: 'Senegal', airport: 'DSS', region: 'Africa' },
    { city: 'Windhoek', country: 'Namibia', airport: 'WDH', region: 'Africa' },
    // Islands - Mediterranean
    { city: 'Ibiza', country: 'Spain', airport: 'IBZ', region: 'Islands' },
    { city: 'Mallorca', country: 'Spain', airport: 'PMI', region: 'Islands' },
    { city: 'Menorca', country: 'Spain', airport: 'MAH', region: 'Islands' },
    { city: 'Formentera', country: 'Spain', airport: 'IBZ', region: 'Islands' },
    { city: 'Tenerife', country: 'Canary Islands', airport: 'TFS', region: 'Islands' },
    { city: 'Lanzarote', country: 'Canary Islands', airport: 'ACE', region: 'Islands' },
    { city: 'Gran Canaria', country: 'Canary Islands', airport: 'LPA', region: 'Islands' },
    { city: 'Fuerteventura', country: 'Canary Islands', airport: 'FUE', region: 'Islands' },
    { city: 'La Palma', country: 'Canary Islands', airport: 'SPC', region: 'Islands' },
    { city: 'Paphos', country: 'Cyprus', airport: 'PFO', region: 'Islands' },
    { city: 'Larnaca', country: 'Cyprus', airport: 'LCA', region: 'Islands' },
    { city: 'Malta', country: 'Malta', airport: 'MLA', region: 'Islands' },
    { city: 'Gozo', country: 'Malta', airport: 'MLA', region: 'Islands' },
    { city: 'Sardinia', country: 'Italy', airport: 'CAG', region: 'Islands' },
    { city: 'Sicily', country: 'Italy', airport: 'CTA', region: 'Islands' },
    { city: 'Corfu', country: 'Greece', airport: 'CFU', region: 'Islands' },
    { city: 'Crete', country: 'Greece', airport: 'HER', region: 'Islands' },
    { city: 'Rhodes', country: 'Greece', airport: 'RHO', region: 'Islands' },
    { city: 'Kos', country: 'Greece', airport: 'KGS', region: 'Islands' },
    { city: 'Zakynthos', country: 'Greece', airport: 'ZTH', region: 'Islands' },
    { city: 'Kefalonia', country: 'Greece', airport: 'EFL', region: 'Islands' },
    { city: 'Madeira', country: 'Portugal', airport: 'FNC', region: 'Islands' },
    { city: 'Azores', country: 'Portugal', airport: 'PDL', region: 'Islands' },
    { city: 'Algarve', country: 'Portugal', airport: 'FAO', region: 'Islands' },
    { city: 'Corsica', country: 'France', airport: 'AJA', region: 'Islands' },
  ];
  
  // Filter destinations based on search text
  const filteredDestinations = destinationSearchText
    ? worldDestinations.filter(dest => 
        dest.city.toLowerCase().includes(destinationSearchText.toLowerCase()) ||
        dest.country.toLowerCase().includes(destinationSearchText.toLowerCase()) ||
        dest.airport.toLowerCase().includes(destinationSearchText.toLowerCase())
      )
    : worldDestinations;
  
  // Calculate nights from dates
  const holidayNights = holidayCheckInDate && holidayCheckOutDate 
    ? Math.ceil((holidayCheckOutDate - holidayCheckInDate) / (1000 * 60 * 60 * 24))
    : 7;
  
  // Generate mock flight data for holiday packages
  const generateFlightData = (departureCity, destination, checkInDate, checkOutDate) => {
    const airlines = [
      { code: 'BA', name: 'British Airways', logo: 'https://www.gstatic.com/flights/airline_logos/70px/BA.png' },
      { code: 'EK', name: 'Emirates', logo: 'https://www.gstatic.com/flights/airline_logos/70px/EK.png' },
      { code: 'QR', name: 'Qatar Airways', logo: 'https://www.gstatic.com/flights/airline_logos/70px/QR.png' },
      { code: 'EY', name: 'Etihad Airways', logo: 'https://www.gstatic.com/flights/airline_logos/70px/EY.png' },
      { code: 'LH', name: 'Lufthansa', logo: 'https://www.gstatic.com/flights/airline_logos/70px/LH.png' }
    ];
    const randomAirline = airlines[Math.floor(Math.random() * airlines.length)];
    const flightNum = Math.floor(Math.random() * 900) + 100;
    
    // Extract airport code from departure city string
    const deptMatch = departureCity.match(/\(([A-Z]{3})\)/);
    const deptCode = deptMatch ? deptMatch[1] : 'LHR';
    const deptName = departureCity.replace(/\s*\([A-Z]{3}\)/, '');
    
    return {
      outbound: {
        airline: randomAirline,
        flightNumber: `${randomAirline.code}${flightNum}`,
        departure: {
          airport: deptName,
          code: deptCode,
          date: checkInDate,
          time: '08:45'
        },
        arrival: {
          airport: `${destination.city} International`,
          code: destination.airport || destination.city.substring(0, 3).toUpperCase(),
          date: checkInDate,
          time: '14:30'
        },
        duration: '5h 45m',
        stops: Math.random() > 0.6 ? 1 : 0,
        stopDetails: Math.random() > 0.6 ? [{ city: 'Amsterdam', code: 'AMS', duration: '1h 30m' }] : [],
        cabinClass: 'Economy',
        baggage: '23kg checked + 7kg cabin'
      },
      return: {
        airline: randomAirline,
        flightNumber: `${randomAirline.code}${flightNum + 1}`,
        departure: {
          airport: `${destination.city} International`,
          code: destination.airport || destination.city.substring(0, 3).toUpperCase(),
          date: checkOutDate,
          time: '16:20'
        },
        arrival: {
          airport: deptName,
          code: deptCode,
          date: checkOutDate,
          time: '22:15'
        },
        duration: '6h 55m',
        stops: Math.random() > 0.6 ? 1 : 0,
        stopDetails: Math.random() > 0.6 ? [{ city: 'Frankfurt', code: 'FRA', duration: '1h 45m' }] : [],
        cabinClass: 'Economy',
        baggage: '23kg checked + 7kg cabin'
      }
    };
  };
  
  // Generate mock hotel data for holiday packages
  const generateHotelData = (destination, stars, nights, checkInDate, checkOutDate, tier) => {
    const hotelNames = {
      3: [`${destination.city} Holiday Inn`, `${destination.city} Comfort Inn`, `${destination.city} Premier Inn`],
      4: [`${destination.city} Marriott`, `${destination.city} Hilton`, `${destination.city} Radisson Blu`],
      5: [`${destination.city} Four Seasons`, `${destination.city} Ritz-Carlton`, `${destination.city} St. Regis`]
    };
    
    const boardBasis = {
      brown: 'Room Only',
      silver: 'Bed & Breakfast',
      gold: 'All Inclusive'
    };
    
    const roomTypes = {
      brown: 'Standard Double Room',
      silver: 'Superior Room',
      gold: 'Deluxe Suite'
    };
    
    const names = hotelNames[stars] || hotelNames[3];
    const randomName = names[Math.floor(Math.random() * names.length)];
    
    return {
      name: randomName,
      stars: stars,
      image: `https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop`,
      location: `${destination.city} City Centre`,
      distance: `${(Math.random() * 3 + 0.5).toFixed(1)} km from city centre`,
      roomType: roomTypes[tier] || 'Standard Room',
      boardBasis: boardBasis[tier] || 'Room Only',
      checkIn: checkInDate,
      checkOut: checkOutDate,
      nights: nights,
      rooms: 1,
      amenities: ['Free WiFi', 'Air Conditioning', 'Swimming Pool', '24h Reception']
    };
  };
  
  // Fetch live holiday package from backend API
  const fetchLiveHolidayPackage = async (hotelStars, tier, tierName, boardBasis) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Extract airport code from departure city
    let originCode = holidayDepartureCity;
    if (holidayDepartureCity.includes('(') && holidayDepartureCity.includes(')')) {
      originCode = holidayDepartureCity.split('(')[1].replace(')', '').trim();
    }
    
    // Get destination code
    const destCode = selectedHolidayDestination.airport || 
                     selectedHolidayDestination.code || 
                     selectedHolidayDestination.city.substring(0, 3).toUpperCase();
    
    const requestData = {
      origin: holidayDepartureCity,
      destination: selectedHolidayDestination.city,
      destination_code: destCode,
      checkin: holidayCheckInDate ? format(holidayCheckInDate, 'yyyy-MM-dd') : format(addDays(new Date(), 14), 'yyyy-MM-dd'),
      checkout: holidayCheckOutDate ? format(holidayCheckOutDate, 'yyyy-MM-dd') : format(addDays(new Date(), 21), 'yyyy-MM-dd'),
      adults: holidayAdults,
      children: holidayChildren,
      infants: holidayInfants,
      children_ages: holidayChildAges.filter(a => a !== '').map(a => parseInt(a)),
      infant_ages: holidayInfantAges.filter(a => a !== '').map(a => parseInt(a)),
      rooms: 1,
      hotel_stars: hotelStars,
      travel_class: 'ECONOMY',
      board_basis: boardBasis,
      currency: 'GBP'
    };
    
    try {
      const response = await axios.post(`${API_URL}/api/holidays/search`, requestData);
      const data = response.data;
      
      if (data.success) {
        const totalGuests = holidayAdults + holidayChildren + holidayInfants;
        
        // Process flight data for display
        let processedFlight = null;
        if (data.flight) {
          const outboundSeg = data.flight.outbound?.segments?.[0] || {};
          const returnSeg = data.flight.return?.segments?.[0] || {};
          
          processedFlight = {
            outbound: {
              airline: outboundSeg.airline || { code: 'XX', name: 'Airline', logo: '' },
              flightNumber: outboundSeg.flight_number || '',
              departure: {
                airport: outboundSeg.departure?.airport || originCode,
                code: outboundSeg.departure?.code || originCode,
                date: holidayCheckInDate,
                time: outboundSeg.departure?.time || '09:00'
              },
              arrival: {
                airport: outboundSeg.arrival?.airport || destCode,
                code: outboundSeg.arrival?.code || destCode,
                date: holidayCheckInDate,
                time: outboundSeg.arrival?.time || '15:00'
              },
              duration: data.flight.outbound?.total_duration || '6h 00m',
              stops: data.flight.outbound?.stops || 0,
              stopDetails: [],
              cabinClass: 'Economy',
              baggage: outboundSeg.baggage || '23kg checked + 7kg cabin'
            },
            return: {
              airline: returnSeg.airline || outboundSeg.airline || { code: 'XX', name: 'Airline', logo: '' },
              flightNumber: returnSeg.flight_number || '',
              departure: {
                airport: returnSeg.departure?.airport || destCode,
                code: returnSeg.departure?.code || destCode,
                date: holidayCheckOutDate,
                time: returnSeg.departure?.time || '16:00'
              },
              arrival: {
                airport: returnSeg.arrival?.airport || originCode,
                code: returnSeg.arrival?.code || originCode,
                date: holidayCheckOutDate,
                time: returnSeg.arrival?.time || '22:00'
              },
              duration: data.flight.return?.total_duration || '7h 00m',
              stops: data.flight.return?.stops || 0,
              stopDetails: [],
              cabinClass: 'Economy',
              baggage: returnSeg.baggage || '23kg checked + 7kg cabin'
            }
          };
        }
        
        // Process hotel data for display
        let processedHotel = null;
        if (data.hotel) {
          processedHotel = {
            name: data.hotel.name,
            stars: data.hotel.stars,
            image: data.hotel.image,
            location: data.hotel.location,
            distance: data.hotel.distance,
            roomType: data.hotel.room_type,
            boardBasis: data.hotel.board_basis,
            checkIn: holidayCheckInDate,
            checkOut: holidayCheckOutDate,
            nights: data.nights,
            rooms: data.hotel.rooms,
            amenities: data.hotel.amenities || []
          };
        }
        
        return {
          destination: selectedHolidayDestination,
          departureCity: holidayDepartureCity,
          checkInDate: holidayCheckInDate,
          checkOutDate: holidayCheckOutDate,
          tier: tier,
          tierName: tierName,
          hotelStars: hotelStars,
          inclusions: tier === 'gold' 
            ? ['Flights', `${hotelStars}-star hotel`, 'All-inclusive board', 'Airport transfers']
            : tier === 'silver' 
            ? ['Flights', `${hotelStars}-star hotel`, 'Breakfast', 'Airport transfers']
            : ['Flights', `${hotelStars}-star hotel`, 'Airport transfers'],
          pricePerPerson: Math.round(data.price_breakdown?.flights?.per_person || 0) + Math.round((data.hotel?.price_per_night || 0) * data.nights / totalGuests),
          totalPrice: Math.round(data.total_price),
          adults: holidayAdults,
          children: holidayChildren,
          childAges: holidayChildAges,
          infants: holidayInfants,
          infantAges: holidayInfantAges,
          guests: totalGuests,
          nights: data.nights,
          flight: processedFlight,
          hotel: processedHotel,
          packageId: data.package_id,
          source: 'live',
          priceBreakdown: data.price_breakdown
        };
      } else {
        throw new Error(data.message || 'Failed to fetch package');
      }
    } catch (error) {
      console.error('Holiday package API error:', error);
      toast.error('Unable to fetch live prices. Please try again.');
      return null;
    }
  };
  
  // Handler for changing children count
  const handleHolidayChildrenChange = (newCount) => {
    if (newCount > holidayChildren) {
      setHolidayChildAges(prev => [...prev, ...Array(newCount - holidayChildren).fill('')]);
    } else {
      setHolidayChildAges(prev => prev.slice(0, newCount));
    }
    setHolidayChildren(newCount);
  };
  
  // Handler for changing infants count
  const handleHolidayInfantsChange = (newCount) => {
    if (newCount > holidayInfants) {
      setHolidayInfantAges(prev => [...prev, ...Array(newCount - holidayInfants).fill('')]);
    } else {
      setHolidayInfantAges(prev => prev.slice(0, newCount));
    }
    setHolidayInfants(newCount);
  };
  
  // Handler for child age change
  const handleHolidayChildAgeChange = (index, value) => {
    setHolidayChildAges(prev => {
      const newAges = [...prev];
      newAges[index] = value;
      return newAges;
    });
  };
  
  // Handler for infant age change
  const handleHolidayInfantAgeChange = (index, value) => {
    setHolidayInfantAges(prev => {
      const newAges = [...prev];
      newAges[index] = value;
      return newAges;
    });
  };
  
  // HAJJ Notice Popup State - DISABLED
  const [showHajjNotice, setShowHajjNotice] = useState(false);
  
  // Handler for header height changes - memoized to prevent infinite loops
  const handleHeaderHeightChange = useCallback((height) => {
    setHeaderHeight(height);
  }, []);
  
  // Show HAJJ Notice popup - DISABLED
  // useEffect(() => {
  //   const hasSeenNotice = sessionStorage.getItem('hajjNoticeShown');
  //   if (!hasSeenNotice) {
  //     const timer = setTimeout(() => {
  //       setShowHajjNotice(true);
  //       sessionStorage.setItem('hajjNoticeShown', 'true');
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  // }, []);
  
  // Scroll to top when major view changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [showResults, showBooking, showFareGrid, showUmrahBuilder, showCustomerDashboard, showAdminDashboard]);
  
  // URL-based state persistence - restore state from URL on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const view = urlParams.get('view');
    const savedState = sessionStorage.getItem('flightSearchState');
    const savedAppState = sessionStorage.getItem('flight380AppState');
    
    // Restore general app state (for views like umrah, manage-booking, etc.)
    if (savedAppState) {
      try {
        const appState = JSON.parse(savedAppState);
        // Restore pinned flights if available
        if (appState.pinnedFlights) setPinnedFlights(appState.pinnedFlights);
        if (appState.pinnedAirlineName) setPinnedAirlineName(appState.pinnedAirlineName);
        if (appState.pinnedAirlineCode) setPinnedAirlineCode(appState.pinnedAirlineCode);
      } catch (e) {
        console.error('Failed to restore app state:', e);
      }
    }
    
    // Restore state based on URL view parameter
    if (view) {
      console.log('Restoring view from URL:', view);
      switch (view) {
        case 'results':
          // Restore search results if available AND not stale (max 10 minutes)
          if (savedState) {
            try {
              const state = JSON.parse(savedState);
              const savedTimestamp = state.savedAt ? new Date(state.savedAt).getTime() : 0;
              const now = Date.now();
              const maxAge = 10 * 60 * 1000; // 10 minutes max age
              
              if (now - savedTimestamp > maxAge) {
                // Results are stale - clear them and show search form
                console.log('Search results are stale (>10 min old), clearing cache');
                sessionStorage.removeItem('flightSearchState');
                toast.info('Your previous search results have expired. Please search again for fresh prices.');
                return; // Don't restore stale results
              }
              
              if (state.searchResults) setSearchResults(state.searchResults);
              if (state.searchParams) setSearchParams(state.searchParams);
              if (state.savedSearchData) setSavedSearchData(state.savedSearchData);
              if (state.isFlexibleSearch !== undefined) setIsFlexibleSearch(state.isFlexibleSearch);
              if (state.pinnedFlights) setPinnedFlights(state.pinnedFlights);
              if (state.pinnedAirlineName) setPinnedAirlineName(state.pinnedAirlineName);
              if (state.pinnedAirlineCode) setPinnedAirlineCode(state.pinnedAirlineCode);
              setShowResults(true);
              console.log('Restored search results from session');
            } catch (e) {
              console.error('Failed to restore search state:', e);
            }
          }
          break;
        case 'booking':
          // Restore booking state if available AND not stale
          if (savedState) {
            try {
              const state = JSON.parse(savedState);
              const savedTimestamp = state.savedAt ? new Date(state.savedAt).getTime() : 0;
              const now = Date.now();
              const maxAge = 10 * 60 * 1000; // 10 minutes max age
              
              if (now - savedTimestamp > maxAge) {
                // Results are stale - redirect to search
                console.log('Booking data is stale (>10 min old), clearing cache');
                sessionStorage.removeItem('flightSearchState');
                toast.error('Your flight selection has expired. Please search again for current availability.');
                return;
              }
              
              if (state.selectedFlight) setSelectedFlight(state.selectedFlight);
              if (state.searchParams) setSearchParams(state.searchParams);
              if (state.savedSearchData) setSavedSearchData(state.savedSearchData);
              setShowBooking(true);
              console.log('Restored booking state from session');
            } catch (e) {
              console.error('Failed to restore booking state:', e);
            }
          }
          break;
        case 'umrah':
          setShowUmrahBuilder(true);
          console.log('Restored Umrah builder view');
          break;
        case 'fare-grid':
          setShowFareGrid(true);
          if (savedState) {
            try {
              const state = JSON.parse(savedState);
              if (state.searchResults) setSearchResults(state.searchResults);
              if (state.searchParams) setSearchParams(state.searchParams);
              if (state.savedSearchData) setSavedSearchData(state.savedSearchData);
              console.log('Restored fare grid state from session');
            } catch (e) {
              console.error('Failed to restore fare grid state:', e);
            }
          }
          break;
        case 'manage-booking':
          setShowManageBooking(true);
          console.log('Restored manage booking view');
          break;
        case 'dashboard':
          setShowCustomerDashboard(true);
          console.log('Restored customer dashboard view');
          break;
        case 'admin':
          setShowAdminDashboard(true);
          console.log('Restored admin dashboard view');
          break;
        default:
          // Home page - clear all views
          console.log('No recognized view, showing home');
          break;
      }
    }
  }, []); // Only run on initial mount
  
  // Save state to sessionStorage when it changes (with timestamp)
  useEffect(() => {
    if (showResults || showBooking || showFareGrid) {
      const stateToSave = {
        searchResults,
        searchParams,
        savedSearchData,
        selectedFlight,
        isFlexibleSearch,
        pinnedFlights,
        pinnedAirlineName,
        pinnedAirlineCode,
        savedAt: new Date().toISOString() // Add timestamp for staleness check
      };
      safeSessionSet('flightSearchState', stateToSave, () => ({ ...stateToSave, searchResults: [], pinnedFlights: [] }));
    }
  }, [searchResults, searchParams, savedSearchData, selectedFlight, isFlexibleSearch, showResults, showBooking, showFareGrid, pinnedFlights, pinnedAirlineName, pinnedAirlineCode]);
  
  // Save general app state for views that don't have search data
  useEffect(() => {
    const appState = {
      pinnedFlights,
      pinnedAirlineName,
      pinnedAirlineCode
    };
    safeSessionSet('flight380AppState', appState, () => ({ pinnedFlights: [], pinnedAirlineName, pinnedAirlineCode }));
  }, [pinnedFlights, pinnedAirlineName, pinnedAirlineCode]);
  
  // Update URL when view changes
  useEffect(() => {
    let newView = '';
    if (showResults) newView = 'results';
    else if (showBooking) newView = 'booking';
    else if (showUmrahBuilder) newView = 'umrah';
    else if (showFareGrid) newView = 'fare-grid';
    else if (showManageBooking) newView = 'manage-booking';
    else if (showCustomerDashboard) newView = 'dashboard';
    else if (showAdminDashboard) newView = 'admin';
    
    const currentParams = new URLSearchParams(location.search);
    const currentView = currentParams.get('view');
    // Shared Umrah package deep link (?package=UP…) — stash before the URL is normalised so UmrahPackages can open it
    if (currentParams.get('package')) sessionStorage.setItem('umrah_shared_package', currentParams.get('package'));
    
    // Only update URL if view has changed and we're not on a special page
    if (newView !== currentView && !location.pathname.includes('/reset-password') && !location.pathname.includes('/umrah-hold-success') && !location.pathname.includes('/umrah-booking-success') && !location.pathname.includes('/flight-payment-success') && !location.pathname.startsWith('/pay-balance/') && !location.pathname.startsWith('/pay-flight-balance/')) {
      if (newView) {
        navigate(`/?view=${newView}`, { replace: true });
      } else if (currentView) {
        navigate('/', { replace: true });
      }
    }
  }, [showResults, showBooking, showUmrahBuilder, showFareGrid, showManageBooking, showCustomerDashboard, showAdminDashboard, navigate, location.search, location.pathname]);
  
  // Check for session_id in URL fragment (Google OAuth callback)
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setIsProcessingAuth(true);
    } else {
      // Check existing session - first from localStorage, then API
      checkAuthSession();
    }
  }, []);

  const checkAuthSession = async () => {
    try {
      // First try to get user from localStorage
      const savedUser = localStorage.getItem('flight380_user');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        console.log('User loaded from localStorage:', parsedUser.email);
        
        // For Google auth users, trust localStorage - don't verify with backend
        // This prevents the 401 from clearing the just-logged-in user
        if (parsedUser.auth_type === 'google') {
          return; // Trust the localStorage for Google users
        }
      }
      
      // For non-Google users or no saved user, verify with backend API
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data) {
        setUser(response.data);
        localStorage.setItem('flight380_user', JSON.stringify(response.data));
      }
    } catch (err) {
      // Only clear if we don't have a valid Google user in localStorage
      const savedUser = localStorage.getItem('flight380_user');
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          if (parsedUser.auth_type === 'google' && parsedUser.email) {
            // Keep Google user logged in even if backend check fails
            console.log('Keeping Google user logged in:', parsedUser.email);
            setUser(parsedUser);
            return;
          }
        } catch (e) {}
      }
      // Not authenticated - clear stale data
      localStorage.removeItem('flight380_user');
      setUser(null);
    }
  };

  const handleAuthSuccess = (userData) => {
    console.log('Auth success:', userData);
    setUser(userData);
    localStorage.setItem('flight380_user', JSON.stringify(userData));
    setIsProcessingAuth(false);
    setShowLogin(false);
    // Force a small delay to ensure state updates before showing toast
    setTimeout(() => {
      toast.success(`Welcome, ${userData.name || userData.email}!`);
    }, 100);
    
    // After successful login, check if there's a pending flight to book
    // This ensures user stays on the same page and continues booking flow
    if (pendingFlight) {
      console.log('Continuing with pending flight after login');
      setTimeout(() => {
        proceedWithBooking(pendingFlight);
      }, 200);
    }
  };

  const handleAuthError = (error) => {
    console.error('Auth error:', error);
    setIsProcessingAuth(false);
    localStorage.removeItem('flight380_user');
    setUser(null);
    // Show specific error message
    const errorMsg = typeof error === 'string' ? error : 'Sign in failed. Please try again.';
    toast.error(errorMsg);
    // Clear the hash from URL if present
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleSignOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Ignore logout errors
    }
    // Always clear local state
    setUser(null);
    localStorage.removeItem('flight380_user');
    toast.success('Signed out successfully');
  };

  // If processing OAuth callback, show AuthCallback
  if (isProcessingAuth) {
    return <AuthCallback onAuthSuccess={handleAuthSuccess} onAuthError={handleAuthError} />;
  }

  // If on reset-password page, show ResetPasswordPage
  if (location.pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }

  // Umrah Quote — the numbered package search form
  if (location.pathname === '/umrah/quote' || location.pathname === '/umrah-quote') {
    return (
      <>
        <Header user={user} onSignOut={handleSignOut} onSignIn={(mode) => { setLoginMode(mode === 'register' ? 'register' : 'login'); setShowLogin(true); }} onHeightChange={handleHeaderHeightChange} onNavigateHome={() => { window.location.href = '/'; }} onManageBooking={() => { window.location.href = '/?view=manage-booking'; }} onMyAccount={() => { window.location.href = '/?view=dashboard'; }} onAdminDashboard={() => { window.location.href = '/?view=admin'; }} selectedCurrency={globalCurrency} onCurrencyChange={setGlobalCurrency} departureAirport={globalDepartureAirport} />
        <section className="pb-16 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900" data-testid="umrah-quote-title">Build your Umrah package</h1>
              <p className="text-sm text-slate-600 mt-1">Flights, Makkah & Madinah hotels, transfers and visa — live prices, ATOL protected.</p>
            </div>
            <UmrahPackages user={user} />
          </div>
        </section>
        <Footer />
      </>
    );
  }
  // Umrah Quotation deposit success page
  if (location.pathname === '/umrah/quote-deposit-success') {
    return (
      <>
        <Header user={user} onLogout={handleSignOut} onLogin={() => setShowLogin(true)} />
        <UmrahQuoteDepositSuccessPage />
        <Footer />
      </>
    );
  }

  // Live flight tracker (AirLabs)
  if (location.pathname === '/track' || location.pathname.startsWith('/track/')) {
    return (
      <>
        <Header user={user} onLogout={handleSignOut} onLogin={() => setShowLogin(true)} />
        <FlightTrackerPage />
        <Footer />
      </>
    );
  }

  // Careers / Jobs page — easyJet-inspired solid orange theme
  if (
    location.pathname === '/careers' ||
    location.pathname.startsWith('/careers/') ||
    location.pathname === '/jobs' ||
    location.pathname.startsWith('/jobs/')
  ) {
    // Rewrite /jobs → /careers so the URL stays canonical.
    if (location.pathname.startsWith('/jobs')) {
      window.history.replaceState({}, '', location.pathname.replace(/^\/jobs/, '/careers'));
    }
    return (
      <>
        <Header user={user} onLogout={handleSignOut} onLogin={() => setShowLogin(true)} />
        <CareersPage />
        <Footer />
      </>
    );
  }
  
  // If on agent-booking page, show AgentBooking
  if (location.pathname === '/agent-booking') {
    return (
      <>
        <Header 
          user={user} 
          onLogout={handleSignOut}
          onLogin={() => setShowLogin(true)}
        />
        <AgentBooking />
        <Footer />
      </>
    );
  }
  
  // Flight card-payment return page (Revolut redirect)
  if (location.pathname === '/flight-payment-success') {
    const bref = new URLSearchParams(location.search).get('bref');
    return (
      <FlightPaymentSuccess
        bookingRef={bref}
        onGoHome={() => {
          navigate('/');
          setShowBooking(false);
          setShowResults(false);
          setShowFareGrid(false);
        }}
      />
    );
  }

  // Umrah deposit balance payment (link emailed with the deposit confirmation)
  if (location.pathname.startsWith('/pay-balance/')) {
    return <PayBalance bookingId={location.pathname.split('/')[2] || ''} token={new URLSearchParams(location.search).get('t') || ''} onGoHome={() => navigate('/')} />;
  }

  // Flight deposit-hold balance payment (link emailed with the reservation confirmation)
  if (location.pathname.startsWith('/pay-flight-balance/')) {
    return <PayFlightBalance pnr={location.pathname.split('/')[2] || ''} token={new URLSearchParams(location.search).get('t') || ''} onGoHome={() => navigate('/')} />;
  }

  // Umrah payment return page (Revolut redirects here with ?bref=<booking ref>)
  if (location.pathname === '/umrah-hold-success' || location.pathname === '/umrah-booking-success') {
    const urlParams = new URLSearchParams(location.search);
    return (
      <HoldBookingSuccess 
        sessionId={urlParams.get('session_id') || urlParams.get('order_id')}
        bookingRef={urlParams.get('bref')}
        onGoHome={() => {
          navigate('/');
          setShowUmrahBuilder(false);
          setShowResults(false);
          setShowBooking(false);
          setShowFareGrid(false);
        }} 
      />
    );
  }
  
  // If on holiday booking success page, confirm and show references
  if (location.pathname === '/booking-success') {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');
    const bookingRef = urlParams.get('ref');
    
    return (
      <HolidayBookingSuccess 
        sessionId={sessionId}
        bookingRef={bookingRef}
        onGoHome={() => {
          navigate('/');
          setHolidayPackageData(null);
        }}
      />
    );
  }

  const handleSearch = async (searchData) => {
    // Check if this is an Umrah search - show Umrah Builder instead
    if (searchData.tripType === 'umrah') {
      setShowUmrahBuilder(true);
      setShowResults(false);
      setShowBooking(false);
      setShowFareGrid(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    console.log('Search data:', searchData);
    setIsLoading(true);
    setSearchError(null);
    setSearchParams(searchData);
    setSavedSearchData(searchData);
    setIsFlexibleSearch(searchData.flexiDates || false);
    setShowBooking(false);
    setSelectedFlight(null);
    setShowUmrahBuilder(false);
    
    // Clear old search results from session storage to ensure fresh data
    sessionStorage.removeItem('flightSearchState');
    
    // Scroll to top immediately on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      let response;
      
      // Handle multi-city search differently
      if (searchData.tripType === 'multi-city' && searchData.legs) {
        response = await axios.post(`${API}/flights/multi-city-search`, {
          legs: searchData.legs,
          adults: searchData.adults,
          youth: searchData.youth,
          children: searchData.children,
          infants: searchData.infants,
          travel_class: searchData.travel_class,
          direct_flights: searchData.direct_flights,
          airline: searchData.airline?.code || null
        });
      } else {
        // Regular one-way or round-trip search
        response = await axios.post(`${API}/flights/search`, {
          origin: searchData.origin,
          destination: searchData.destination,
          origin_airports: searchData.origin_airports || null,
          destination_airports: searchData.destination_airports || null,
          departure_date: searchData.departure_date,
          return_date: searchData.return_date,
          adults: searchData.adults,
          youth: searchData.youth,
          children: searchData.children,
          infants: searchData.infants,
          travel_class: searchData.travel_class,
          direct_flights: searchData.direct_flights,
          flexible_dates: searchData.flexiDates || false,
          airline: searchData.airline?.code || null,
          // Enhanced filters
          max_stops: searchData.max_stops || null,
          preferred_airlines: searchData.preferred_airlines || null,
          excluded_airlines: searchData.excluded_airlines || null,
          max_price: searchData.max_price || null,
          departure_time_range: searchData.departure_time_range || null,
          refundable_only: searchData.refundable_only || null,
          // New filters - Baggage and Connection Length
          baggage_filter: searchData.baggage_filter || null,
          connection_outbound: searchData.connection_outbound || null,
          connection_return: searchData.connection_return || null
        });
      }
      
      if (response.data.success) {
        const isMultiCityResponse = response.data.type === 'multi-city';
        // Multi-city: every item is a complete Sabre BFM itinerary (all sectors, one fare).
        const flights = isMultiCityResponse ? (response.data.itineraries || []) : (response.data.flights || []);
        const pinned = isMultiCityResponse ? [] : (response.data.pinned_flights || []);
        const meta = response.data.meta || {};
        setSearchResults(flights);
        setPinnedFlights(pinned);
        setPinnedAirlineName(meta.pinned_airline_name || null);
        setPinnedAirlineCode(meta.pinned_airline_code || null);
        setMultiCityNote(isMultiCityResponse ? (meta.note || null) : null);
        setShowResults(true);
        
        const totalFlights = flights.length + pinned.length;
        if (isMultiCityResponse) {
          if (totalFlights > 0) toast.success(`Found ${totalFlights} complete multi-city itineraries priced by Sabre`);
          else toast.info(meta.note || 'No complete multi-city itinerary found for these sectors and dates.', { duration: 6000 });
        } else if (totalFlights > 0) {
          const flexiMessage = searchData.flexiDates ? ' (including ±3 days)' : '';
          const pinnedMessage = pinned.length > 0 ? ` (${pinned.length} ${meta.pinned_airline_name || 'direct'} flights)` : '';
          toast.success(`Found ${totalFlights} flights for you${pinnedMessage}${flexiMessage}!`);
        } else {
          // Show results page with "no flights" message instead of staying on blank page
          const directFlightsMsg = searchData.direct_flights ? ' Try unchecking "Direct flights only" to see connecting flights.' : '';
          toast.info(`No flights found for this route.${directFlightsMsg}`, { duration: 5000 });
        }
        
        // Scroll to top of results
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        // Handle specific error types
        const errorType = response.data.error;
        const errorMessage = response.data.message || 'No flights found. Please try different dates or destinations.';
        
        if (errorType === 'past_date') {
          // Past date error - show clear message and don't navigate away
          toast.error(errorMessage, { duration: 6000 });
          setSearchError(errorMessage);
          // Don't show results page, keep user on search form
          return;
        } else if (errorType === 'invalid_dates') {
          toast.error(errorMessage, { duration: 5000 });
          setSearchError(errorMessage);
          return;
        } else {
          // Generic error - show results page so user can modify search
          setSearchResults([]);
          setPinnedFlights([]);
          setShowResults(true);
          toast.error(errorMessage);
          setSearchError(response.data.error);
        }
      }
    } catch (error) {
      console.error('Flight search error:', error);
      toast.error('Failed to search flights. Please try again.');
      setSearchError(error.message);
      setSearchResults([]);
      setPinnedFlights([]);
      // Keep showing results page so user can modify search
      setShowResults(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectFlight = async (flight) => {
    console.log('Selected flight:', flight);
    setRevalidationError(null);
    
    // Check if user is logged in or in guest mode
    if (!user && !isGuestMode) {
      // Store the flight and show auth prompt
      setPendingFlight(flight);
      setShowAuthPrompt(true);
      return;
    }
    
    // Revalidate flight before proceeding
    await revalidateAndProceed(flight);
  };

  // Revalidate flight availability in real-time
  const revalidateAndProceed = async (flight) => {
    if (flight?.source === 'duffel') {
      // Low-cost (Duffel) fares go through the fare-brand flow
      setDuffelFlight(flight);
      setShowResults(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsRevalidating(true);
    setRevalidationError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/flights/revalidate`, {
        flight_data: flight,
        // Same pax mix as the search so the re-priced fare is like-for-like (youth travel on adult fares).
        passengers: searchParams?.passengers || {
          adults: (searchParams?.adults || 1) + (searchParams?.youth || 0),
          children: searchParams?.children || 0,
          infants: searchParams?.infants || 0,
        },
        cabin_class: searchParams?.travel_class || flight.cabin_class || 'economy'
      });
      
      const result = response.data;
      
      if (result.valid && result.available) {
        // Check if verification was skipped (API issue but we allow proceeding)
        if (result.check_skipped) {
          // Don't show alarming message - just proceed silently
          console.log('Flight verification skipped, proceeding with booking');
        } else if (result.price_changed && result.current_price) {
          // Flight is available - check for price changes
          const priceChange = result.price_change;
          if (priceChange > 0) {
            toast.warning(`Price increased by £${priceChange.toFixed(2)} since your search`, { duration: 5000 });
          } else if (priceChange < 0) {
            toast.success(`Good news! Price decreased by £${Math.abs(priceChange).toFixed(2)}`, { duration: 5000 });
          }
          // Update flight with new price
          flight = result.flight || { ...flight, price: result.current_price };
        }
        
        // Proceed with booking
        if (!result.check_skipped) {
          toast.success(`Flight verified! ${flight.airline} - £${Math.round(result.current_price || flight.price)}`);
        }
        setSelectedFlight(result.flight || flight);
        setShowBooking(true);
        setShowResults(false);
        setShowFareGrid(false);
        window.scrollTo(0, 0);
      } else {
        // Flight not available — silently refresh live fares instead of showing an error banner
        toast.info('Fares just updated — refreshing live availability for you…', { duration: 4000 });
        setShowFareGrid(false);
        if (searchParams) {
          await handleSearch(searchParams);
        }
      }
    } catch (error) {
      console.error('Revalidation error:', error);
      // On API error, allow user to proceed (fail gracefully)
      // Don't show alarming message - availability will be confirmed during actual booking
      setSelectedFlight(flight);
      setShowBooking(true);
      setShowResults(false);
      setShowFareGrid(false);
      window.scrollTo(0, 0);
    } finally {
      setIsRevalidating(false);
    }
  };

  // Handle continuing with booking after auth choice
  const proceedWithBooking = async (flight, fromAuth = true) => {
    // Set flag to skip itinerary review when coming from auth flow
    setSkipItineraryReview(fromAuth);
    // Revalidate before proceeding
    await revalidateAndProceed(flight);
    setShowAuthPrompt(false);
    setPendingFlight(null);
  };

  // Handle auth prompt actions
  const handleAuthPromptSignIn = () => {
    // Keep pendingFlight - don't clear it so we can proceed after login
    setShowAuthPrompt(false);
    setShowLogin(true);
  };

  const handleAuthPromptSignUp = () => {
    // Keep pendingFlight - don't clear it so we can proceed after login
    setShowAuthPrompt(false);
    setShowLogin(true);
  };

  const handleContinueAsGuest = () => {
    setIsGuestMode(true);
    if (pendingFlight) {
      proceedWithBooking(pendingFlight, true);
    }
  };

  const handleAuthPromptClose = () => {
    setShowAuthPrompt(false);
    setPendingFlight(null);
  };

  const handleBackToResults = () => {
    setShowBooking(false);
    setSelectedFlight(null);
    setShowResults(true);
    setShowFareGrid(false);
  };

  const handleBookingComplete = () => {
    setShowBooking(false);
    setSelectedFlight(null);
    setShowResults(false);
    setShowFareGrid(false);
    setSearchResults([]);
    setPinnedFlights([]);
    setSavedSearchData(null);
    setSearchParams(null);
    setSkipItineraryReview(false);
    sessionStorage.removeItem('flightSearchState');
  };

  // Navigate to Home - resets all state and scrolls to home
  const handleNavigateHome = () => {
    setShowBooking(false);
    setSelectedFlight(null);
    setShowResults(false);
    setShowFareGrid(false);
    setShowUmrahBuilder(false); // Reset Umrah Builder state
    setShowManageBooking(false); // Reset Manage Booking state
    setShowCustomerDashboard(false); // Reset Customer Dashboard state
    setShowAdminDashboard(false); // Reset Admin Dashboard state
    setIsLoading(false);
    setSearchError(null);
    setSearchResults([]);
    setPinnedFlights([]);
    setSavedSearchData(null);
    setSearchParams(null);
    setSkipItineraryReview(false);
    sessionStorage.removeItem('flightSearchState');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  // Navigate to Manage Booking
  const handleManageBooking = () => {
    setShowManageBooking(true);
    setShowBooking(false);
    setSelectedFlight(null);
    setShowResults(false);
    setShowFareGrid(false);
    setShowUmrahBuilder(false);
    setShowCustomerDashboard(false);
    setIsLoading(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Navigate to Customer Dashboard (My Account)
  const handleMyAccount = () => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    setShowCustomerDashboard(true);
    setShowManageBooking(false);
    setShowBooking(false);
    setSelectedFlight(null);
    setShowResults(false);
    setShowFareGrid(false);
    setShowUmrahBuilder(false);
    setIsLoading(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show Fare Calendar Grid
  const handleShowFareGrid = () => {
    setShowFareGrid(true);
    setShowResults(false);
    window.scrollTo(0, 0);
  };

  // Handle selecting dates from fare grid
  const handleSelectDatesFromGrid = async (depDate, retDate) => {
    // Update search params with new dates
    const updatedParams = {
      ...savedSearchData,
      departure_date: depDate,
      return_date: retDate
    };
    
    // Trigger new search with selected dates
    setShowFareGrid(false);
    handleSearch(updatedParams);
  };

  // Back to results from fare grid
  const handleBackFromFareGrid = () => {
    setShowFareGrid(false);
    setShowResults(true);
  };

  // Open Admin Dashboard
  const handleAdminDashboard = () => {
    setShowResults(false);
    setShowBooking(false);
    setShowFareGrid(false);
    setShowUmrahBuilder(false);
    setShowManageBooking(false);
    setShowCustomerDashboard(false);
    setShowAdminDashboard(true);
  };

  const iconMap = {
    BadgePoundSterling,
    Zap,
    Headphones,
    CalendarClock,
    Shield,
    Gift
  };

  // Handle modify search - scroll back to search form
  const handleModifySearch = () => {
    document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <Header 
        onNavigateHome={handleNavigateHome} 
        user={user}
        onSignIn={(mode) => { setLoginMode(mode === 'register' ? 'register' : 'login'); setShowLogin(true); }}
        onSignOut={handleSignOut}
        onHeightChange={handleHeaderHeightChange}
        onManageBooking={handleManageBooking}
        onMyAccount={handleMyAccount}
        onAdminDashboard={handleAdminDashboard}
        selectedCurrency={globalCurrency}
        onCurrencyChange={setGlobalCurrency}
        departureAirport={globalDepartureAirport}
      />
      
      {/* WhatsApp Button */}
      <WhatsAppButton />

      {/* AI Travel Concierge — global multi-stage chat widget */}
      <AIChatWidget />

      {/* Umrah Package Builder — new feature announcement (one-time per browser) */}
      {!showResults && !showBooking && !duffelFlight && !showFareGrid && !showUmrahBuilder && !showCustomerDashboard && !showAdminDashboard && !showManageBooking && !showLogin && (
        <UmrahAnnouncementModal onStartBuilder={() => setShowUmrahBuilder(true)} />
      )}
      
      {/* Login Modal */}
      {showLogin && (
        <LoginPage 
          initialMode={loginMode}
          onClose={() => {
            setShowLogin(false);
            // If user closes login without signing in but had a pending flight,
            // show the auth prompt again so they can choose Guest option
            if (pendingFlight) {
              setShowAuthPrompt(true);
            }
          }}
          onLoginSuccess={(userData) => {
            setUser(userData);
            setShowLogin(false);
            toast.success(`Welcome, ${userData.name || userData.email}!`);
            // If there's a pending flight from auth prompt, proceed with booking
            // Skip itinerary review since user already saw it before auth prompt
            if (pendingFlight) {
              proceedWithBooking(pendingFlight, true);
            }
          }}
        />
      )}

      {/* Holiday Package Builder Modal - Flights + Hotels Search */}
      {showHolidayPackageBuilder && selectedHolidayDestination && (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="holiday-package-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-4 overflow-hidden">
            {/* Hero Header with Destination Image */}
            <div className="relative h-48 md:h-64 overflow-hidden">
              <img 
                src={selectedHolidayDestination.imageUrl} 
                alt={selectedHolidayDestination.city}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30"></div>
              
              {/* Close Button */}
              <button 
                onClick={() => {
                  setShowHolidayPackageBuilder(false);
                  setSelectedHolidayDestination(null);
                  setHolidayPackageData(null);
                  setHolidayPackageEditMode(null);
                }}
                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
              
              {/* Destination Info */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <Badge className="bg-white/20 text-white border-white/30 mb-2">
                  Flights + Hotels Package
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-1">
                  {selectedHolidayDestination.city}
                </h2>
                <p className="text-white/80">{selectedHolidayDestination.country}</p>
              </div>
            </div>
            
            {/* Search Bar Section - Editable Fields */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* From - Editable */}
                <div className="bg-white/10 rounded-xl p-3">
                  <label className="text-xs text-white/70 block mb-1">From</label>
                  <div className="flex items-center gap-2">
                    <Plane className="h-4 w-4 text-white" />
                    <select 
                      value={holidayDepartureCity}
                      onChange={(e) => setHolidayDepartureCity(e.target.value)}
                      className="bg-transparent text-white font-semibold border-none outline-none cursor-pointer w-full appearance-none"
                      style={{ backgroundImage: 'none' }}
                    >
                      <optgroup label="London Airports" className="text-slate-900">
                        <option value="London Heathrow (LHR)" className="text-slate-900">London Heathrow (LHR)</option>
                        <option value="London Gatwick (LGW)" className="text-slate-900">London Gatwick (LGW)</option>
                        <option value="London Stansted (STN)" className="text-slate-900">London Stansted (STN)</option>
                        <option value="London Luton (LTN)" className="text-slate-900">London Luton (LTN)</option>
                        <option value="London City (LCY)" className="text-slate-900">London City (LCY)</option>
                        <option value="London Southend (SEN)" className="text-slate-900">London Southend (SEN)</option>
                      </optgroup>
                      <optgroup label="England" className="text-slate-900">
                        <option value="Manchester (MAN)" className="text-slate-900">Manchester (MAN)</option>
                        <option value="Birmingham (BHX)" className="text-slate-900">Birmingham (BHX)</option>
                        <option value="Bristol (BRS)" className="text-slate-900">Bristol (BRS)</option>
                        <option value="Newcastle (NCL)" className="text-slate-900">Newcastle (NCL)</option>
                        <option value="Liverpool (LPL)" className="text-slate-900">Liverpool (LPL)</option>
                        <option value="Leeds Bradford (LBA)" className="text-slate-900">Leeds Bradford (LBA)</option>
                        <option value="East Midlands (EMA)" className="text-slate-900">East Midlands (EMA)</option>
                        <option value="Southampton (SOU)" className="text-slate-900">Southampton (SOU)</option>
                        <option value="Bournemouth (BOH)" className="text-slate-900">Bournemouth (BOH)</option>
                        <option value="Exeter (EXT)" className="text-slate-900">Exeter (EXT)</option>
                        <option value="Norwich (NWI)" className="text-slate-900">Norwich (NWI)</option>
                        <option value="Doncaster Sheffield (DSA)" className="text-slate-900">Doncaster Sheffield (DSA)</option>
                        <option value="Humberside (HUY)" className="text-slate-900">Humberside (HUY)</option>
                        <option value="Durham Tees Valley (MME)" className="text-slate-900">Durham Tees Valley (MME)</option>
                        <option value="Newquay (NQY)" className="text-slate-900">Newquay (NQY)</option>
                      </optgroup>
                      <optgroup label="Scotland" className="text-slate-900">
                        <option value="Edinburgh (EDI)" className="text-slate-900">Edinburgh (EDI)</option>
                        <option value="Glasgow (GLA)" className="text-slate-900">Glasgow (GLA)</option>
                        <option value="Glasgow Prestwick (PIK)" className="text-slate-900">Glasgow Prestwick (PIK)</option>
                        <option value="Aberdeen (ABZ)" className="text-slate-900">Aberdeen (ABZ)</option>
                        <option value="Inverness (INV)" className="text-slate-900">Inverness (INV)</option>
                        <option value="Dundee (DND)" className="text-slate-900">Dundee (DND)</option>
                      </optgroup>
                      <optgroup label="Wales" className="text-slate-900">
                        <option value="Cardiff (CWL)" className="text-slate-900">Cardiff (CWL)</option>
                      </optgroup>
                      <optgroup label="Northern Ireland" className="text-slate-900">
                        <option value="Belfast International (BFS)" className="text-slate-900">Belfast International (BFS)</option>
                        <option value="Belfast City (BHD)" className="text-slate-900">Belfast City (BHD)</option>
                        <option value="City of Derry (LDY)" className="text-slate-900">City of Derry (LDY)</option>
                      </optgroup>
                      <optgroup label="Channel Islands" className="text-slate-900">
                        <option value="Jersey (JER)" className="text-slate-900">Jersey (JER)</option>
                        <option value="Guernsey (GCI)" className="text-slate-900">Guernsey (GCI)</option>
                      </optgroup>
                      <optgroup label="Isle of Man" className="text-slate-900">
                        <option value="Isle of Man (IOM)" className="text-slate-900">Isle of Man (IOM)</option>
                      </optgroup>
                    </select>
                    <ChevronDown className="h-4 w-4 text-white/70" />
                  </div>
                </div>
                {/* To - Fixed */}
                <div className="bg-white/10 rounded-xl p-3">
                  <label className="text-xs text-white/70 block mb-1">To</label>
                  <div className="flex items-center gap-2 text-white font-semibold">
                    <MapPin className="h-4 w-4" />
                    <span>{selectedHolidayDestination.city}</span>
                  </div>
                </div>
                {/* Dates - Calendar Picker */}
                <Popover open={holidayDatePickerOpen} onOpenChange={setHolidayDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <div className="bg-white/10 rounded-xl p-3 cursor-pointer hover:bg-white/20 transition-colors">
                      <label className="text-xs text-white/70 block mb-1">Dates</label>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-white" />
                        <span className="text-white font-semibold">
                          {holidayCheckInDate && holidayCheckOutDate 
                            ? `${format(holidayCheckInDate, 'dd MMM')} - ${format(holidayCheckOutDate, 'dd MMM')} (${holidayNights} nights)`
                            : 'Select dates'
                          }
                        </span>
                        <ChevronDown className="h-4 w-4 text-white/70 ml-auto" />
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[300]" align="center">
                    <HolidayCalendar
                      checkInDate={holidayCheckInDate}
                      checkOutDate={holidayCheckOutDate}
                      onDateChange={({ checkIn, checkOut }) => {
                        setHolidayCheckInDate(checkIn);
                        setHolidayCheckOutDate(checkOut);
                      }}
                      onClose={() => setHolidayDatePickerOpen(false)}
                    />
                  </PopoverContent>
                </Popover>
                
                {/* Guests - Umrah Builder Pattern */}
                <Popover open={holidayGuestsOpen} onOpenChange={setHolidayGuestsOpen}>
                  <PopoverTrigger asChild>
                    <div className="bg-white/10 rounded-xl p-3 cursor-pointer hover:bg-white/20 transition-colors">
                      <label className="text-xs text-white/70 block mb-1">Guests</label>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-white" />
                        <span className="text-white font-semibold">
                          {holidayAdults} Adult{holidayAdults > 1 ? 's' : ''}
                          {holidayChildren > 0 && `, ${holidayChildren} Child${holidayChildren > 1 ? 'ren' : ''}`}
                          {holidayInfants > 0 && `, ${holidayInfants} Infant${holidayInfants > 1 ? 's' : ''}`}
                          {`, ${holidayRooms} Room${holidayRooms > 1 ? 's' : ''}`}
                        </span>
                        <ChevronDown className="h-4 w-4 text-white/70 ml-auto" />
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-4 z-[300]" align="start">
                    <div className="space-y-4">
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Adults</div>
                          <div className="text-xs text-slate-500">16+ years</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50" 
                            onClick={() => setHolidayAdults(Math.max(1, holidayAdults - 1))} 
                            disabled={holidayAdults <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{holidayAdults}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50" 
                            onClick={() => setHolidayAdults(Math.min(9 - holidayChildren - holidayInfants, holidayAdults + 1))} 
                            disabled={holidayAdults + holidayChildren + holidayInfants >= 9}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Children */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">Children</div>
                            <div className="text-xs text-slate-500">2–11 years</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50" 
                              onClick={() => handleHolidayChildrenChange(Math.max(0, holidayChildren - 1))} 
                              disabled={holidayChildren <= 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">{holidayChildren}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50" 
                              onClick={() => handleHolidayChildrenChange(Math.min(9 - holidayAdults - holidayInfants, holidayChildren + 1))} 
                              disabled={holidayAdults + holidayChildren + holidayInfants >= 9}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Child Age Dropdowns */}
                        {holidayChildren > 0 && (
                          <div className="grid grid-cols-2 gap-2">
                            {holidayChildAges.map((age, index) => (
                              <select 
                                key={`child-${index}`}
                                value={age}
                                onChange={(e) => handleHolidayChildAgeChange(index, e.target.value)}
                                className={`h-10 px-3 rounded-md border ${!age ? 'border-2 border-red-400 text-red-500' : 'border-slate-300'} bg-white text-sm`}
                              >
                                <option value="">Age needed</option>
                                {[2,3,4,5,6,7,8,9,10,11].map((ageOption) => (
                                  <option key={ageOption} value={String(ageOption)}>
                                    {ageOption} years old
                                  </option>
                                ))}
                              </select>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Infants */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">Infants</div>
                            <div className="text-xs text-slate-500">0–1 year</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50" 
                              onClick={() => handleHolidayInfantsChange(Math.max(0, holidayInfants - 1))} 
                              disabled={holidayInfants <= 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">{holidayInfants}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50" 
                              onClick={() => handleHolidayInfantsChange(Math.min(Math.min(holidayAdults, 9 - holidayAdults - holidayChildren), holidayInfants + 1))} 
                              disabled={holidayInfants >= holidayAdults || holidayAdults + holidayChildren + holidayInfants >= 9}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Infant Age Dropdowns */}
                        {holidayInfants > 0 && (
                          <div className="grid grid-cols-2 gap-2">
                            {holidayInfantAges.map((age, index) => (
                              <select 
                                key={`infant-${index}`}
                                value={age}
                                onChange={(e) => handleHolidayInfantAgeChange(index, e.target.value)}
                                className={`h-10 px-3 rounded-md border ${!age ? 'border-2 border-red-400 text-red-500' : 'border-slate-300'} bg-white text-sm`}
                              >
                                <option value="">Age needed</option>
                                {[0,1].map((ageOption) => (
                                  <option key={ageOption} value={String(ageOption)}>
                                    {ageOption} years old
                                  </option>
                                ))}
                              </select>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Rooms — controls how many hotel rooms are reserved */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <div>
                          <div className="font-medium">Rooms</div>
                          <div className="text-xs text-slate-500">Number of hotel rooms</div>
                        </div>
                        <div className="flex items-center gap-2" data-testid="holiday-rooms-selector">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50"
                            onClick={() => setHolidayRooms(Math.max(1, holidayRooms - 1))}
                            disabled={holidayRooms <= 1}
                            data-testid="holiday-rooms-decrement"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span
                            className="w-8 text-center font-medium"
                            data-testid="holiday-rooms-count"
                          >
                            {holidayRooms}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-sm border-slate-300 text-blue-600 hover:bg-blue-50"
                            onClick={() => setHolidayRooms(Math.min(9, holidayRooms + 1))}
                            disabled={holidayRooms >= 9 || holidayRooms >= (holidayAdults + holidayChildren)}
                            data-testid="holiday-rooms-increment"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Helper Text */}
                      {(holidayChildren > 0 || holidayInfants > 0) && (
                        <p className="text-xs text-slate-500">
                          To find you a place to stay that fits your entire group along with correct prices, we need to know how old your children will be at check-out
                        </p>
                      )}
                      
                      {/* Done Button */}
                      <Button 
                        className="w-full mt-2" 
                        onClick={() => setHolidayGuestsOpen(false)}
                      >
                        Done
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            {/* Package Tiers Section */}
            <div className="p-6">
              {/* Edit Mode Banner */}
              {holidayPackageEditMode && (
                <div className={`mb-4 p-4 rounded-xl flex items-center gap-3 ${
                  holidayPackageEditMode === 'flights' 
                    ? 'bg-blue-50 border border-blue-200' 
                    : 'bg-purple-50 border border-purple-200'
                }`}>
                  <div className={`p-2 rounded-full ${
                    holidayPackageEditMode === 'flights' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    {holidayPackageEditMode === 'flights' 
                      ? <Plane className="h-5 w-5 text-blue-600" />
                      : <Building2 className="h-5 w-5 text-purple-600" />
                    }
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${
                      holidayPackageEditMode === 'flights' ? 'text-blue-800' : 'text-purple-800'
                    }`}>
                      {holidayPackageEditMode === 'flights' ? 'Editing Flights' : 'Editing Hotel'}
                    </p>
                    <p className="text-sm text-slate-600">
                      Select a package below to update your {holidayPackageEditMode === 'flights' ? 'flight' : 'hotel'} selection. 
                      Your previous selection will be replaced.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowHolidayPackageBuilder(false);
                      setHolidayPackageEditMode(null);
                    }}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </Button>
                </div>
              )}
              
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {holidayPackageEditMode ? 'Update Your Package' : 'Choose Your Package'}
              </h3>
              <p className="text-slate-600 mb-6">Select the perfect holiday package for your trip to {selectedHolidayDestination.city}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Brown Package - Economy */}
                <div className="relative border-2 border-amber-800 rounded-2xl overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-b from-amber-50 to-white">
                  <div className="bg-gradient-to-r from-amber-800 to-amber-900 p-4 text-center">
                    <span className="text-3xl mb-1 block"></span>
                    <h4 className="text-lg font-bold text-white">Brown Package</h4>
                    <p className="text-amber-200 text-sm">Economy</p>
                  </div>
                  <div className="p-5">
                    <div className="text-center mb-4">
                      <span className="text-3xl font-bold text-amber-900"><Money gbp={Math.round(selectedHolidayDestination.price * holidayNights * 0.8)} /></span>
                      <span className="text-slate-500 text-sm"> / per person</span>
                      <p className="text-xs text-slate-400 mt-1">Total: <Money gbp={Math.round(selectedHolidayDestination.price * holidayNights * 0.8 * (holidayAdults + holidayChildren * 0.7 + holidayInfants * 0.1))} /> for {holidayAdults + holidayChildren + holidayInfants} guests</p>
                    </div>
                    <ul className="space-y-3 mb-5">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-800 flex-shrink-0" />
                        <span>Return flights included</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-800 flex-shrink-0" />
                        <span>⭐ 3-star hotel ({holidayNights} nights)</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-800 flex-shrink-0" />
                        <span>Airport transfers</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-400">
                        <X className="h-5 w-5 flex-shrink-0" />
                        <span>Breakfast not included</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-900 hover:to-amber-950 text-white"
                      onClick={async () => {
                        setIsLoadingHolidayPackages(true);
                        toast.loading('Searching for live prices...', { id: 'package-search' });
                        
                        const packageData = await fetchLiveHolidayPackage(3, 'brown', 'Brown Package (Economy)', 'room_only');
                        
                        if (packageData) {
                          setHolidayPackageData(packageData);
                          setShowHolidayPackageBuilder(false);
                          setHolidayPackageEditMode(null);
                          toast.success(`${selectedHolidayDestination.city} Brown Package ${holidayPackageEditMode ? 'updated' : 'selected'}!`, { id: 'package-search' });
                        }
                        setIsLoadingHolidayPackages(false);
                      }}
                    >
                      {holidayPackageEditMode ? 'Update Package' : 'Select Package'}
                    </Button>
                  </div>
                </div>
                
                {/* Silver Package - Standard */}
                <div className="relative border-2 border-slate-400 rounded-2xl overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-b from-slate-100 to-white">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-b-lg">
                    POPULAR
                  </div>
                  <div className="bg-gradient-to-r from-slate-500 to-slate-600 p-4 text-center">
                    <span className="text-3xl mb-1 block"></span>
                    <h4 className="text-lg font-bold text-white">Silver Package</h4>
                    <p className="text-slate-200 text-sm">Standard</p>
                  </div>
                  <div className="p-5">
                    <div className="text-center mb-4">
                      <span className="text-3xl font-bold text-slate-700"><Money gbp={Math.round(selectedHolidayDestination.price * holidayNights)} /></span>
                      <span className="text-slate-500 text-sm"> / per person</span>
                      <p className="text-xs text-slate-400 mt-1">Total: <Money gbp={Math.round(selectedHolidayDestination.price * holidayNights * (holidayAdults + holidayChildren * 0.7 + holidayInfants * 0.1))} /> for {holidayAdults + holidayChildren + holidayInfants} guests</p>
                    </div>
                    <ul className="space-y-3 mb-5">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-slate-600 flex-shrink-0" />
                        <span>Return flights included</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-slate-600 flex-shrink-0" />
                        <span>⭐ 4-star hotel ({holidayNights} nights)</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-slate-600 flex-shrink-0" />
                        <span>Daily breakfast</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-slate-600 flex-shrink-0" />
                        <span>Airport transfers</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white"
                      onClick={async () => {
                        setIsLoadingHolidayPackages(true);
                        toast.loading('Searching for live prices...', { id: 'package-search' });
                        
                        const packageData = await fetchLiveHolidayPackage(4, 'silver', 'Silver Package (Standard)', 'breakfast');
                        
                        if (packageData) {
                          setHolidayPackageData(packageData);
                          setShowHolidayPackageBuilder(false);
                          setHolidayPackageEditMode(null);
                          toast.success(`${selectedHolidayDestination.city} Silver Package ${holidayPackageEditMode ? 'updated' : 'selected'}!`, { id: 'package-search' });
                        }
                        setIsLoadingHolidayPackages(false);
                      }}
                    >
                      {holidayPackageEditMode ? 'Update Package' : 'Select Package'}
                    </Button>
                  </div>
                </div>
                
                {/* Gold Package - Premium All Inclusive */}
                <div className="relative border-2 border-amber-600 rounded-2xl overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-b from-amber-100 to-white ring-2 ring-amber-600/50">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-600 to-amber-700 text-white text-xs font-bold px-3 py-1 rounded-b-lg">
                    BEST VALUE
                  </div>
                  <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-4 text-center">
                    <span className="text-3xl mb-1 block"></span>
                    <h4 className="text-lg font-bold text-white">Gold Package</h4>
                    <p className="text-amber-200 text-sm">Premium All-Inclusive</p>
                  </div>
                  <div className="p-5">
                    <div className="text-center mb-4">
                      <span className="text-3xl font-bold text-amber-700"><Money gbp={Math.round(selectedHolidayDestination.price * holidayNights * 1.3)} /></span>
                      <span className="text-slate-500 text-sm"> / per person</span>
                      <p className="text-xs text-slate-400 mt-1">Total: <Money gbp={Math.round(selectedHolidayDestination.price * holidayNights * 1.3 * (holidayAdults + holidayChildren * 0.7 + holidayInfants * 0.1))} /> for {holidayAdults + holidayChildren + holidayInfants} guests</p>
                    </div>
                    <ul className="space-y-3 mb-5">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <span>Return flights included</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <span className="font-semibold">⭐ 5-star hotel ({holidayNights} nights)</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <span className="font-semibold">All-inclusive board</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <span>Airport transfers</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                      onClick={async () => {
                        setIsLoadingHolidayPackages(true);
                        toast.loading('Searching for live prices...', { id: 'package-search' });
                        
                        const packageData = await fetchLiveHolidayPackage(5, 'gold', 'Gold Package (Premium All-Inclusive)', 'all_inclusive');
                        
                        if (packageData) {
                          setHolidayPackageData(packageData);
                          setShowHolidayPackageBuilder(false);
                          setHolidayPackageEditMode(null);
                          toast.success(`${selectedHolidayDestination.city} Gold Package ${holidayPackageEditMode ? 'updated' : 'selected'}!`, { id: 'package-search' });
                        }
                        setIsLoadingHolidayPackages(false);
                      }}
                    >
                      {holidayPackageEditMode ? 'Update Package' : 'Select Package'}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-6 mt-8 pt-6 border-t">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  <span>ATOL Protected</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span>Free Cancellation</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                  <span>Secure Payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading Overlay for Holiday Packages */}
      {isLoadingHolidayPackages && (
        <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center animate-pulse">
              <Plane className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Preparing Your Package</h3>
            <p className="text-slate-300">Finding the best deals...</p>
          </div>
        </div>
      )}
      
      {/* Explore All Destinations Page */}
      {showExploreDestinations && (
        <div className="fixed inset-0 z-[200] bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 overflow-y-auto" data-testid="explore-destinations-page">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-white">Explore All Destinations</h1>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowExploreDestinations(false)}
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Search Bar Section - Same pattern as Dubai/Paris packages */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 shadow-xl">
              <h2 className="text-xl font-bold text-white mb-4 text-center">Find Your Perfect Holiday</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* From - Editable */}
                <div className="bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-colors">
                  <label className="text-xs text-white/70 block mb-2">From</label>
                  <div className="flex items-center gap-2">
                    <Plane className="h-5 w-5 text-white" />
                    <select 
                      value={holidayDepartureCity}
                      onChange={(e) => setHolidayDepartureCity(e.target.value)}
                      className="bg-transparent text-white font-semibold border-none outline-none cursor-pointer w-full appearance-none text-sm"
                    >
                      <optgroup label="London Airports" className="text-slate-900">
                        <option value="London Heathrow (LHR)" className="text-slate-900">London Heathrow (LHR)</option>
                        <option value="London Gatwick (LGW)" className="text-slate-900">London Gatwick (LGW)</option>
                        <option value="London Stansted (STN)" className="text-slate-900">London Stansted (STN)</option>
                        <option value="London Luton (LTN)" className="text-slate-900">London Luton (LTN)</option>
                        <option value="London City (LCY)" className="text-slate-900">London City (LCY)</option>
                        <option value="London Southend (SEN)" className="text-slate-900">London Southend (SEN)</option>
                      </optgroup>
                      <optgroup label="England" className="text-slate-900">
                        <option value="Manchester (MAN)" className="text-slate-900">Manchester (MAN)</option>
                        <option value="Birmingham (BHX)" className="text-slate-900">Birmingham (BHX)</option>
                        <option value="Bristol (BRS)" className="text-slate-900">Bristol (BRS)</option>
                        <option value="Newcastle (NCL)" className="text-slate-900">Newcastle (NCL)</option>
                        <option value="Liverpool (LPL)" className="text-slate-900">Liverpool (LPL)</option>
                        <option value="Leeds Bradford (LBA)" className="text-slate-900">Leeds Bradford (LBA)</option>
                        <option value="East Midlands (EMA)" className="text-slate-900">East Midlands (EMA)</option>
                        <option value="Southampton (SOU)" className="text-slate-900">Southampton (SOU)</option>
                        <option value="Bournemouth (BOH)" className="text-slate-900">Bournemouth (BOH)</option>
                        <option value="Exeter (EXT)" className="text-slate-900">Exeter (EXT)</option>
                        <option value="Norwich (NWI)" className="text-slate-900">Norwich (NWI)</option>
                      </optgroup>
                      <optgroup label="Scotland" className="text-slate-900">
                        <option value="Edinburgh (EDI)" className="text-slate-900">Edinburgh (EDI)</option>
                        <option value="Glasgow (GLA)" className="text-slate-900">Glasgow (GLA)</option>
                        <option value="Glasgow Prestwick (PIK)" className="text-slate-900">Glasgow Prestwick (PIK)</option>
                        <option value="Aberdeen (ABZ)" className="text-slate-900">Aberdeen (ABZ)</option>
                        <option value="Inverness (INV)" className="text-slate-900">Inverness (INV)</option>
                      </optgroup>
                      <optgroup label="Wales" className="text-slate-900">
                        <option value="Cardiff (CWL)" className="text-slate-900">Cardiff (CWL)</option>
                      </optgroup>
                      <optgroup label="Northern Ireland" className="text-slate-900">
                        <option value="Belfast International (BFS)" className="text-slate-900">Belfast International (BFS)</option>
                        <option value="Belfast City (BHD)" className="text-slate-900">Belfast City (BHD)</option>
                      </optgroup>
                    </select>
                    <ChevronDown className="h-4 w-4 text-white/70" />
                  </div>
                </div>
                
                {/* Destination Search - Searchable Dropdown */}
                <Popover open={destinationSearchOpen} onOpenChange={setDestinationSearchOpen}>
                  <PopoverTrigger asChild>
                    <div className="bg-white/10 rounded-xl p-4 cursor-pointer hover:bg-white/20 transition-colors">
                      <label className="text-xs text-white/70 block mb-2">Destination</label>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-white" />
                        <span className="text-white font-semibold text-sm">
                          {selectedDestinationCity || 'Search destinations...'}
                        </span>
                        <ChevronDown className="h-4 w-4 text-white/70 ml-auto" />
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0 z-[300]" align="start">
                    <div className="p-3 border-b">
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search by city, country or airport code..."
                          value={destinationSearchText}
                          onChange={(e) => setDestinationSearchText(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {/* All Destinations Option */}
                      <div 
                        className="px-4 py-3 hover:bg-slate-100 cursor-pointer border-b flex items-center gap-3"
                        onClick={() => {
                          setSelectedDestinationCity('');
                          setExploreSearchQuery('');
                          setDestinationSearchOpen(false);
                          setDestinationSearchText('');
                        }}
                      >
                        <Globe className="h-5 w-5 text-blue-500" />
                        <div>
                          <div className="font-medium text-slate-900">All Destinations</div>
                          <div className="text-xs text-slate-500">Browse all available packages</div>
                        </div>
                      </div>
                      
                      {/* Filtered Results */}
                      {filteredDestinations.length > 0 ? (
                        <>
                          {/* Group by region */}
                          {['Popular', 'Europe', 'Americas', 'Asia', 'Middle East', 'Africa', 'Islands'].map(region => {
                            const regionDests = filteredDestinations.filter(d => d.region === region);
                            if (regionDests.length === 0) return null;
                            return (
                              <div key={region}>
                                <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 sticky top-0">
                                  {region === 'Popular' && '⭐ '}
                                  {region === 'Europe' && '🇪🇺 '}
                                  {region === 'Americas' && '🌴 '}
                                  {region === 'Asia' && '🏝️ '}
                                  {region === 'Middle East' && '🕌 '}
                                  {region === 'Africa' && '🌍 '}
                                  {region === 'Islands' && '🏖️ '}
                                  {region}
                                </div>
                                {regionDests.map((dest, idx) => (
                                  <div 
                                    key={`${dest.city}-${idx}`}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center justify-between"
                                    onClick={() => {
                                      setSelectedDestinationCity(`${dest.city}, ${dest.country}`);
                                      setExploreSearchQuery(dest.city);
                                      setDestinationSearchOpen(false);
                                      setDestinationSearchText('');
                                    }}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                        {dest.city.charAt(0)}
                                      </div>
                                      <div>
                                        <div className="font-medium text-slate-900">{dest.city}</div>
                                        <div className="text-xs text-slate-500">{dest.country}</div>
                                      </div>
                                    </div>
                                    <div className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-1 rounded">
                                      {dest.airport}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="px-4 py-8 text-center text-slate-500">
                          <MapPin className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                          <p>No destinations found</p>
                          <p className="text-xs mt-1">Try a different search term</p>
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                
                {/* Dates - Calendar Picker */}
                <Popover open={holidayDatePickerOpen} onOpenChange={setHolidayDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <div className="bg-white/10 rounded-xl p-4 cursor-pointer hover:bg-white/20 transition-colors">
                      <label className="text-xs text-white/70 block mb-2">Dates</label>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-white" />
                        <span className="text-white font-semibold text-sm">
                          {holidayCheckInDate && holidayCheckOutDate 
                            ? `${format(holidayCheckInDate, 'dd MMM')} - ${format(holidayCheckOutDate, 'dd MMM')} (${holidayNights} nights)`
                            : 'Select dates'
                          }
                        </span>
                        <ChevronDown className="h-4 w-4 text-white/70 ml-auto" />
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[300]" align="center">
                    <HolidayCalendar
                      checkInDate={holidayCheckInDate}
                      checkOutDate={holidayCheckOutDate}
                      onDateChange={({ checkIn, checkOut }) => {
                        setHolidayCheckInDate(checkIn);
                        setHolidayCheckOutDate(checkOut);
                      }}
                      onClose={() => setHolidayDatePickerOpen(false)}
                    />
                  </PopoverContent>
                </Popover>
                
                {/* Guests - Umrah Builder Pattern */}
                <Popover open={holidayGuestsOpen} onOpenChange={setHolidayGuestsOpen}>
                  <PopoverTrigger asChild>
                    <div className="bg-white/10 rounded-xl p-4 cursor-pointer hover:bg-white/20 transition-colors">
                      <label className="text-xs text-white/70 block mb-2">Guests</label>
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-white" />
                        <span className="text-white font-semibold text-sm">
                          {holidayAdults} Adult{holidayAdults > 1 ? 's' : ''}
                          {holidayChildren > 0 && `, ${holidayChildren} Child${holidayChildren > 1 ? 'ren' : ''}`}
                          {holidayInfants > 0 && `, ${holidayInfants} Infant${holidayInfants > 1 ? 's' : ''}`}
                        </span>
                        <ChevronDown className="h-4 w-4 text-white/70 ml-auto" />
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-[340px] p-4 z-[300]" align="center">
                    <div className="space-y-4">
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Adults</div>
                          <div className="text-xs text-slate-500">16+ years</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setHolidayAdults(Math.max(1, holidayAdults - 1))} disabled={holidayAdults <= 1}>
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{holidayAdults}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setHolidayAdults(Math.min(9, holidayAdults + 1))} disabled={holidayAdults >= 9}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Children */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Children</div>
                          <div className="text-xs text-slate-500">2-11 years</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleHolidayChildrenChange(Math.max(0, holidayChildren - 1))} disabled={holidayChildren <= 0}>
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{holidayChildren}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleHolidayChildrenChange(Math.min(6, holidayChildren + 1))} disabled={holidayChildren >= 6}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Infants */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Infants</div>
                          <div className="text-xs text-slate-500">0-1 years</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleHolidayInfantsChange(Math.max(0, holidayInfants - 1))} disabled={holidayInfants <= 0}>
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{holidayInfants}</span>
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleHolidayInfantsChange(Math.min(holidayAdults, holidayInfants + 1))} disabled={holidayInfants >= holidayAdults}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <Button className="w-full mt-2" onClick={() => setHolidayGuestsOpen(false)}>Done</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* View Packages Button */}
              <div className="mt-6 flex justify-center">
                <Button 
                  size="lg"
                  data-testid="view-packages-btn"
                  className="bg-white text-blue-600 hover:bg-white/90 font-bold text-lg px-12 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    // If a destination is selected, open the package builder
                    if (selectedDestinationCity || exploreSearchQuery) {
                      // First try to match in popularDestinations (has full package data)
                      const searchTerm = selectedDestinationCity || exploreSearchQuery;
                      let matchedDest = popularDestinations.find(d => 
                        d.city.toLowerCase() === searchTerm.split(',')[0].toLowerCase().trim() ||
                        d.city.toLowerCase().includes(searchTerm.toLowerCase())
                      );
                      
                      // If not found in popular, check worldDestinations and create a package
                      if (!matchedDest) {
                        const worldMatch = worldDestinations.find(d => 
                          d.city.toLowerCase() === searchTerm.split(',')[0].toLowerCase().trim() ||
                          d.city.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        if (worldMatch) {
                          // Create a destination package for the world destination
                          matchedDest = {
                            id: `world-${worldMatch.airport}`,
                            city: worldMatch.city,
                            country: worldMatch.country,
                            description: `Explore ${worldMatch.city}`,
                            price: 299,
                            imageUrl: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop'
                          };
                        }
                      }
                      
                      if (matchedDest) {
                        setSelectedHolidayDestination(matchedDest);
                        setShowExploreDestinations(false);
                        setShowHolidayPackageBuilder(true);
                      }
                    }
                  }}
                  disabled={!selectedDestinationCity && !exploreSearchQuery}
                >
                  <Plane className="h-5 w-5 mr-2" />
                  View Packages
                </Button>
              </div>
            </div>
          </div>
          
          {/* Destinations Grid */}
          <div className="max-w-7xl mx-auto px-4 pb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularDestinations
                .filter(dest => 
                  exploreSearchQuery === '' || 
                  dest.city.toLowerCase().includes(exploreSearchQuery.toLowerCase()) ||
                  dest.country.toLowerCase().includes(exploreSearchQuery.toLowerCase())
                )
                .map((destination, index) => {
                const cardStyles = [
                  { gradient: 'from-rose-500 to-pink-600', border: 'hover:border-rose-400' },
                  { gradient: 'from-violet-500 to-purple-600', border: 'hover:border-violet-400' },
                  { gradient: 'from-slate-700 to-slate-900', border: 'hover:border-slate-500' },
                  { gradient: 'from-amber-500 to-orange-600', border: 'hover:border-amber-400' },
                  { gradient: 'from-yellow-500 to-amber-600', border: 'hover:border-yellow-400' },
                  { gradient: 'from-teal-500 to-cyan-600', border: 'hover:border-teal-400' },
                ];
                const style = cardStyles[index % cardStyles.length];
                
                return (
                  <div 
                    key={destination.id} 
                    className={`group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer hover:-translate-y-2 border-2 border-transparent ${style.border}`}
                    onClick={() => {
                      setSelectedHolidayDestination(destination);
                      setShowExploreDestinations(false);
                      setShowHolidayPackageBuilder(true);
                    }}
                  >
                    {/* Image Section */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={destination.imageUrl} 
                        alt={destination.city}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                      
                      {/* Price Badge */}
                      <div className={`absolute top-4 right-4 bg-gradient-to-r ${style.gradient} text-white font-bold px-4 py-2 rounded-full shadow-lg text-sm`}>
                        From <Money gbp={destination.price} />
                      </div>
                      
                      {/* Destination Info */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-xl font-bold text-white mb-1">{destination.city}</h3>
                        <p className="text-white/90 text-sm">{destination.country}</p>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <p className="text-slate-600 mb-3 text-sm">{destination.description}</p>
                      
                      {/* Package Tiers Preview */}
                      <div className="flex items-center gap-2 mb-3 text-xs">
                        <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-medium">Economy</span>
                        <span className="bg-slate-200 text-slate-700 px-2 py-1 rounded-full font-medium">Standard</span>
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">Premium</span>
                      </div>
                      
                      {/* CTA Button */}
                      <button className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-r ${style.gradient} text-white font-semibold hover:opacity-90 transition-all text-sm`}>
                        <span>View Packages</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* No Results */}
            {exploreSearchQuery && popularDestinations.filter(dest => 
              dest.city.toLowerCase().includes(exploreSearchQuery.toLowerCase()) ||
              dest.country.toLowerCase().includes(exploreSearchQuery.toLowerCase())
            ).length === 0 && (
              <div className="text-center py-12">
                <p className="text-white/70 text-lg">No destinations found for "{exploreSearchQuery}"</p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-white/30 text-white hover:bg-white/10"
                  onClick={() => setExploreSearchQuery('')}
                >
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Holiday Package Summary Modal */}
      {holidayPackageData && !showHolidayPackageBuilder && (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="holiday-summary-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-white/20 text-white border-white/30 mb-2">
                    {holidayPackageData.tierName}
                  </Badge>
                  <h2 className="text-2xl font-bold">{holidayPackageData.destination.city} Holiday Package</h2>
                  <p className="text-emerald-100">{holidayPackageData.nights} nights • {holidayPackageData.guests} guests</p>
                </div>
                <button 
                  onClick={() => setHolidayPackageData(null)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Package Details */}
            <div className="p-6 space-y-6">
              {/* Inclusions */}
              <div>
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  Package Inclusions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {holidayPackageData.inclusions.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Flight Details - Enhanced */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-blue-50 p-4 flex items-center justify-between">
                  <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                    <Plane className="h-5 w-5" />
                    Flight Details
                  </h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-blue-600 border-blue-300 hover:bg-blue-100"
                    onClick={() => {
                      toast.info('Opening Umrah Builder to edit flights...', { duration: 1500 });
                      // Close Holiday Package Summary and open Umrah Builder at Step 2 (Flights)
                      setHolidayPackageData(null);
                      setHolidayPackageEditMode(null);
                      setShowUmrahBuilder(true);
                    }}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Edit Flights
                  </Button>
                </div>
                
                {holidayPackageData.flight ? (
                  <div className="p-4 space-y-4">
                    {/* Outbound Flight */}
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <img 
                          src={holidayPackageData.flight.outbound.airline.logo} 
                          alt={holidayPackageData.flight.outbound.airline.name}
                          className="h-8 w-8 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div>
                          <p className="font-semibold text-slate-900">{holidayPackageData.flight.outbound.airline.name}</p>
                          <p className="text-xs text-slate-500">Flight {holidayPackageData.flight.outbound.flightNumber}</p>
                        </div>
                        <Badge className="ml-auto bg-blue-100 text-blue-700 border-blue-200">Outbound</Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 items-center">
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{holidayPackageData.flight.outbound.departure.time}</p>
                          <p className="font-medium text-slate-700">{holidayPackageData.flight.outbound.departure.airport}</p>
                          <p className="text-sm text-slate-500">({holidayPackageData.flight.outbound.departure.code})</p>
                          <p className="text-xs text-slate-400">{holidayPackageData.checkInDate ? format(holidayPackageData.checkInDate, 'EEE, dd MMM yyyy') : ''}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-slate-500 mb-1">{holidayPackageData.flight.outbound.duration}</p>
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-px bg-slate-300 flex-1"></div>
                            <Plane className="h-4 w-4 text-blue-500 rotate-90" />
                            <div className="h-px bg-slate-300 flex-1"></div>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {holidayPackageData.flight.outbound.stops === 0 ? 'Direct' : `${holidayPackageData.flight.outbound.stops} Stop`}
                          </p>
                          {holidayPackageData.flight.outbound.stopDetails?.length > 0 && (
                            <p className="text-xs text-amber-600">via {holidayPackageData.flight.outbound.stopDetails[0].city}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900">{holidayPackageData.flight.outbound.arrival.time}</p>
                          <p className="font-medium text-slate-700">{holidayPackageData.flight.outbound.arrival.airport}</p>
                          <p className="text-sm text-slate-500">({holidayPackageData.flight.outbound.arrival.code})</p>
                          <p className="text-xs text-slate-400">{holidayPackageData.checkInDate ? format(holidayPackageData.checkInDate, 'EEE, dd MMM yyyy') : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-3 pt-3 border-t text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Class:</span> {holidayPackageData.flight.outbound.cabinClass}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Baggage:</span> {holidayPackageData.flight.outbound.baggage}
                        </span>
                      </div>
                    </div>
                    
                    {/* Return Flight */}
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <img 
                          src={holidayPackageData.flight.return.airline.logo} 
                          alt={holidayPackageData.flight.return.airline.name}
                          className="h-8 w-8 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div>
                          <p className="font-semibold text-slate-900">{holidayPackageData.flight.return.airline.name}</p>
                          <p className="text-xs text-slate-500">Flight {holidayPackageData.flight.return.flightNumber}</p>
                        </div>
                        <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-200">Return</Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 items-center">
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{holidayPackageData.flight.return.departure.time}</p>
                          <p className="font-medium text-slate-700">{holidayPackageData.flight.return.departure.airport}</p>
                          <p className="text-sm text-slate-500">({holidayPackageData.flight.return.departure.code})</p>
                          <p className="text-xs text-slate-400">{holidayPackageData.checkOutDate ? format(holidayPackageData.checkOutDate, 'EEE, dd MMM yyyy') : ''}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-slate-500 mb-1">{holidayPackageData.flight.return.duration}</p>
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-px bg-slate-300 flex-1"></div>
                            <Plane className="h-4 w-4 text-emerald-500 rotate-90" />
                            <div className="h-px bg-slate-300 flex-1"></div>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {holidayPackageData.flight.return.stops === 0 ? 'Direct' : `${holidayPackageData.flight.return.stops} Stop`}
                          </p>
                          {holidayPackageData.flight.return.stopDetails?.length > 0 && (
                            <p className="text-xs text-amber-600">via {holidayPackageData.flight.return.stopDetails[0].city}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-slate-900">{holidayPackageData.flight.return.arrival.time}</p>
                          <p className="font-medium text-slate-700">{holidayPackageData.flight.return.arrival.airport}</p>
                          <p className="text-sm text-slate-500">({holidayPackageData.flight.return.arrival.code})</p>
                          <p className="text-xs text-slate-400">{holidayPackageData.checkOutDate ? format(holidayPackageData.checkOutDate, 'EEE, dd MMM yyyy') : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-3 pt-3 border-t text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Class:</span> {holidayPackageData.flight.return.cabinClass}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Baggage:</span> {holidayPackageData.flight.return.baggage}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-500">
                    <p>Flight details will be displayed here</p>
                  </div>
                )}
              </div>
              
              {/* Hotel Details - Enhanced */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-purple-50 p-4 flex items-center justify-between">
                  <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                    <Hotel className="h-5 w-5" />
                    Accommodation
                  </h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-purple-600 border-purple-300 hover:bg-purple-100"
                    onClick={() => {
                      toast.info('Opening Umrah Builder to edit hotel...', { duration: 1500 });
                      // Close Holiday Package Summary and open Umrah Builder at Step 3 (Hotels)
                      setHolidayPackageData(null);
                      setHolidayPackageEditMode(null);
                      setShowUmrahBuilder(true);
                    }}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Edit Hotel
                  </Button>
                </div>
                
                {holidayPackageData.hotel ? (
                  <div className="p-4">
                    <div className="flex gap-4">
                      {/* Hotel Image */}
                      <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={holidayPackageData.hotel.image} 
                          alt={holidayPackageData.hotel.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Hotel Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-bold text-slate-900 text-lg">{holidayPackageData.hotel.name}</h5>
                            <div className="flex items-center gap-1 mt-1">
                              {[...Array(holidayPackageData.hotel.stars)].map((_, i) => (
                                <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {holidayPackageData.hotel.location} • {holidayPackageData.hotel.distance}
                        </p>
                      </div>
                    </div>
                    
                    {/* Hotel Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Room Type</p>
                        <p className="font-medium text-slate-900 text-sm">{holidayPackageData.hotel.roomType}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Board Basis</p>
                        <p className="font-medium text-slate-900 text-sm">{holidayPackageData.hotel.boardBasis}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Check-in</p>
                        <p className="font-medium text-slate-900 text-sm">
                          {holidayPackageData.checkInDate ? format(holidayPackageData.checkInDate, 'dd MMM yyyy') : '-'}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">Check-out</p>
                        <p className="font-medium text-slate-900 text-sm">
                          {holidayPackageData.checkOutDate ? format(holidayPackageData.checkOutDate, 'dd MMM yyyy') : '-'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-slate-600">
                        <span className="font-medium">{holidayPackageData.hotel.nights} nights</span> • {holidayPackageData.hotel.rooms} room
                      </span>
                      <span className="text-slate-400">|</span>
                      <span className="text-slate-600">
                        {holidayPackageData.adults} Adult{holidayPackageData.adults > 1 ? 's' : ''}
                        {holidayPackageData.children > 0 && `, ${holidayPackageData.children} Child${holidayPackageData.children > 1 ? 'ren' : ''}`}
                        {holidayPackageData.infants > 0 && `, ${holidayPackageData.infants} Infant${holidayPackageData.infants > 1 ? 's' : ''}`}
                      </span>
                    </div>
                    
                    {/* Amenities */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {holidayPackageData.hotel.amenities?.map((amenity, idx) => (
                        <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-500">
                    <p>Hotel details will be displayed here</p>
                  </div>
                )}
              </div>
              
              {/* Price Summary */}
              <div className="bg-gradient-to-r from-slate-50 to-emerald-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3">Price Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Price per person</span>
                    <span className="font-semibold"><Money gbp={holidayPackageData.pricePerPerson} /></span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">
                      {holidayPackageData.adults} Adult{holidayPackageData.adults > 1 ? 's' : ''}
                      {holidayPackageData.children > 0 && ` + ${holidayPackageData.children} Child${holidayPackageData.children > 1 ? 'ren' : ''}`}
                      {holidayPackageData.infants > 0 && ` + ${holidayPackageData.infants} Infant${holidayPackageData.infants > 1 ? 's' : ''}`}
                    </span>
                    <span className="font-semibold">x {holidayPackageData.guests}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg pt-3 border-t border-slate-200">
                    <span className="font-bold text-slate-900">Total Price</span>
                    <span className="font-bold text-emerald-600 text-2xl"><Money gbp={holidayPackageData.totalPrice} /></span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setHolidayPackageData(null);
                    setShowHolidayPackageBuilder(true);
                  }}
                >
                  Change Package
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                  onClick={() => {
                    // Initialize passenger array based on guest count
                    const totalAdults = holidayPackageData.adults || 2;
                    const totalChildren = holidayPackageData.children || 0;
                    const passengers = [];
                    
                    for (let i = 0; i < totalAdults; i++) {
                      passengers.push({
                        type: 'adult',
                        title: '',
                        first_name: '',
                        last_name: '',
                        date_of_birth: '',
                        gender: '',
                        passport_number: '',
                        passport_expiry: ''
                      });
                    }
                    
                    for (let i = 0; i < totalChildren; i++) {
                      passengers.push({
                        type: 'child',
                        title: '',
                        first_name: '',
                        last_name: '',
                        date_of_birth: '',
                        gender: ''
                      });
                    }
                    
                    setHolidayPassengers(passengers);
                    
                    // Pre-fill with user data if available
                    if (user) {
                      setHolidayContactFirstName(user.firstName || '');
                      setHolidayContactLastName(user.lastName || '');
                      setHolidayContactEmail(user.email || '');
                      setHolidayContactPhone(user.phone || '');
                    }
                    
                    setShowHolidayCustomerDetails(true);
                  }}
                >
                  Continue to Booking
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HAJJ 2026 Important Notice Popup */}
      {showHajjNotice && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="hajj-notice-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Important Umrah Travel Notice</h2>
                  <p className="text-emerald-100 text-sm">HAJJ 2026 Season Restrictions</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="p-1.5 bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-amber-900">Last Exit Date</p>
                    <p className="text-sm text-amber-700">All Umrah passengers must return on or before <strong>18th April 2026</strong></p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="p-1.5 bg-blue-100 rounded-full flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900">Booking Window</p>
                    <p className="text-sm text-blue-700">Customers can make bookings up to <strong>2nd April 2026</strong></p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-200">
                  <div className="p-1.5 bg-red-100 rounded-full flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-red-900">Entry Restricted</p>
                    <p className="text-sm text-red-700">Entry into Makkah and Madinah is not permitted from <strong>3rd April 2026</strong> to <strong>4th June 2026</strong> due to HAJJ 2026</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <p className="text-sm text-emerald-700 leading-relaxed">
                  <strong className="text-emerald-800">Umrah Bookings Resume:</strong> Customers can book Umrah packages for travel dates <strong>on or after 5th June 2026</strong>.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-sm text-slate-600 leading-relaxed">
                  <strong className="text-slate-800">Please Note:</strong> For travel before April 2026, ensure your return flight is scheduled on or before 18th April 2026.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <Button 
                onClick={() => setShowHajjNotice(false)}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 rounded-xl"
              >
                I Understand, Continue to Booking
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Package Customer Details Modal */}
      {showHolidayCustomerDetails && holidayPackageData && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Passenger Details</h2>
                  <p className="text-emerald-100 text-sm">{holidayPackageData.destination?.city} Holiday Package</p>
                </div>
                <button
                  onClick={() => setShowHolidayCustomerDetails(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Lead Passenger / Contact Details */}
              <Card className="border-0 shadow-md">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3 rounded-t-lg">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Lead Passenger & Contact Details
                  </h3>
                </div>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">First Name *</label>
                      <input
                        type="text"
                        placeholder="First name"
                        value={holidayContactFirstName}
                        onChange={(e) => setHolidayContactFirstName(e.target.value)}
                        className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Last Name *</label>
                      <input
                        type="text"
                        placeholder="Last name"
                        value={holidayContactLastName}
                        onChange={(e) => setHolidayContactLastName(e.target.value)}
                        className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-500" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        placeholder="email@example.com"
                        value={holidayContactEmail}
                        onChange={(e) => setHolidayContactEmail(e.target.value)}
                        className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Phone className="h-4 w-4 text-slate-500" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        placeholder="+44 7XXX XXXXXX"
                        value={holidayContactPhone}
                        onChange={(e) => setHolidayContactPhone(e.target.value)}
                        className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Passenger Details */}
              {holidayPassengers.map((passenger, index) => (
                <Card key={index} className="border-0 shadow-md">
                  <div className={`px-4 py-3 rounded-t-lg ${
                    passenger.type === 'adult' 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500' 
                      : 'bg-gradient-to-r from-amber-500 to-orange-500'
                  }`}>
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      {passenger.type === 'adult' ? `Adult ${index + 1}` : `Child ${index - (holidayPackageData.adults || 2) + 1}`}
                      <Badge className="bg-white/20 text-white border-white/30 ml-auto">
                        {passenger.type === 'adult' ? '12+ years' : '2-11 years'}
                      </Badge>
                    </h3>
                  </div>
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Title *</label>
                        <select
                          value={passenger.title}
                          onChange={(e) => {
                            const updated = [...holidayPassengers];
                            updated[index].title = e.target.value;
                            setHolidayPassengers(updated);
                          }}
                          className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                          <option value="">Select</option>
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Ms">Ms</option>
                          <option value="Miss">Miss</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">First Name *</label>
                        <input
                          type="text"
                          placeholder="First name"
                          value={passenger.first_name}
                          onChange={(e) => {
                            const updated = [...holidayPassengers];
                            updated[index].first_name = e.target.value;
                            setHolidayPassengers(updated);
                          }}
                          className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Last Name *</label>
                        <input
                          type="text"
                          placeholder="Last name"
                          value={passenger.last_name}
                          onChange={(e) => {
                            const updated = [...holidayPassengers];
                            updated[index].last_name = e.target.value;
                            setHolidayPassengers(updated);
                          }}
                          className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Gender *</label>
                        <select
                          value={passenger.gender}
                          onChange={(e) => {
                            const updated = [...holidayPassengers];
                            updated[index].gender = e.target.value;
                            setHolidayPassengers(updated);
                          }}
                          className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                          <option value="">Select</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Date of Birth *</label>
                        <input
                          type="date"
                          value={passenger.date_of_birth}
                          onChange={(e) => {
                            const updated = [...holidayPassengers];
                            updated[index].date_of_birth = e.target.value;
                            setHolidayPassengers(updated);
                          }}
                          className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      {passenger.type === 'adult' && (
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Passport Number</label>
                          <input
                            type="text"
                            placeholder="Optional"
                            value={passenger.passport_number || ''}
                            onChange={(e) => {
                              const updated = [...holidayPassengers];
                              updated[index].passport_number = e.target.value;
                              setHolidayPassengers(updated);
                            }}
                            className="w-full h-11 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Booking Summary */}
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-emerald-800">Total Package Price</p>
                      <p className="text-sm text-emerald-600">{holidayPackageData.destination?.city} • {holidayPackageData.hotel?.name}</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700"><Money gbp={holidayPackageData.totalPrice} /></p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Footer Actions */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowHolidayCustomerDetails(false)}
              >
                Back to Summary
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                disabled={isProcessingHolidayBooking || !holidayContactFirstName || !holidayContactLastName || !holidayContactEmail || !holidayContactPhone}
                onClick={async () => {
                  // Validate all passengers have required fields
                  const incompletePassenger = holidayPassengers.find(p => !p.title || !p.first_name || !p.last_name || !p.gender || !p.date_of_birth);
                  if (incompletePassenger) {
                    toast.error('Please complete all passenger details');
                    return;
                  }
                  
                  try {
                    setIsProcessingHolidayBooking(true);
                    toast.loading('Creating your booking...', { id: 'holiday-booking' });
                    
                    // Prepare booking request with all customer details
                    const bookingData = {
                      package_id: holidayPackageData.packageId || `HPK-${Date.now()}`,
                      package_tier: holidayPackageData.tier || 'silver',
                      destination: holidayPackageData.destination?.city || 'Dubai',
                      checkin: holidayPackageData.checkInDate ? format(holidayPackageData.checkInDate, 'yyyy-MM-dd') : '',
                      checkout: holidayPackageData.checkOutDate ? format(holidayPackageData.checkOutDate, 'yyyy-MM-dd') : '',
                      adults: holidayPackageData.adults || 2,
                      children: holidayPackageData.children || 0,
                      infants: holidayPackageData.infants || 0,
                      rooms: holidayPackageData.rooms || 1,
                      flight_segments: holidayPackageData.flight?.outbound?.segments || [],
                      flight_price: holidayPackageData.flight?.price || 0,
                      hotel_id: holidayPackageData.hotel?.id || null,
                      hotel_name: holidayPackageData.hotel?.name || 'Hotel',
                      hotel_price: holidayPackageData.hotel?.totalPrice || 0,
                      room_type: holidayPackageData.hotel?.roomType || 'Standard Room',
                      board_basis: holidayPackageData.hotel?.boardBasis || 'breakfast',
                      include_transport: true,
                      transport_type: 'standard',
                      contact_email: holidayContactEmail,
                      contact_phone: holidayContactPhone,
                      contact_name: `${holidayContactFirstName} ${holidayContactLastName}`,
                      passengers: holidayPassengers,
                      total_price: holidayPackageData.totalPrice || 0
                    };
                    
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/holidays/book`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(bookingData)
                    });
                    
                    const result = await response.json();
                    
                    if (result.success && result.stripe_checkout_url) {
                      toast.success('Redirecting to secure payment...', { id: 'holiday-booking' });
                      // Store booking ref and contact email for confirmation emails
                      localStorage.setItem('pending_holiday_booking', result.booking_ref);
                      localStorage.setItem('holiday_contact_email', holidayContactEmail);
                      // Redirect to Stripe checkout
                      window.location.href = result.stripe_checkout_url;
                    } else {
                      toast.error(result.detail || 'Failed to create booking', { id: 'holiday-booking' });
                    }
                  } catch (error) {
                    console.error('Booking error:', error);
                    toast.error('Failed to process booking. Please try again.', { id: 'holiday-booking' });
                  } finally {
                    setIsProcessingHolidayBooking(false);
                  }
                }}
              >
                {isProcessingHolidayBooking ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Prompt Modal - Shows when user tries to book without being logged in */}
      {showAuthPrompt && (
        <AuthPromptModal
          onClose={handleAuthPromptClose}
          onSignIn={handleAuthPromptSignIn}
          onSignUp={handleAuthPromptSignUp}
          onContinueAsGuest={handleContinueAsGuest}
          flightDetails={pendingFlight}
        />
      )}

      {/* Manage Booking */}
      {showManageBooking && (
        <section className="pb-16 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <ManageBooking onBack={handleNavigateHome} />
        </section>
      )}

      {/* Customer Dashboard */}
      {showCustomerDashboard && user && (
        <section className="pb-16 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <CustomerDashboard 
            user={user}
            onBack={handleNavigateHome}
            onManageBooking={handleManageBooking}
          />
        </section>
      )}

      {/* Admin Dashboard */}
      {showAdminDashboard && (
        <section style={{ paddingTop: `${headerHeight}px` }}>
          <AdminDashboard onBack={handleNavigateHome} />
        </section>
      )}

      {/* Booking Flow */}
      {duffelFlight && !showBooking && (
        <section className="pb-16 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <DuffelBookingFlow
            flight={duffelFlight}
            onBack={() => { setDuffelFlight(null); setShowResults(true); }}
          />
        </section>
      )}

      {showBooking && selectedFlight && (
        <section className="pb-16 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <BookingFlow 
            flight={selectedFlight}
            searchParams={savedSearchData}
            onBack={handleBackToResults}
            onComplete={handleBookingComplete}
            skipItineraryReview={skipItineraryReview}
          />
        </section>
      )}

      {/* Fare Calendar Grid - 7x7 Date Combinations */}
      {showFareGrid && savedSearchData && (
        <section className="min-h-screen" style={{ paddingTop: `${headerHeight}px` }}>
          {savedSearchData.return_date ? (
            <FareCalendarGrid 
              origin={savedSearchData.origin}
              destination={savedSearchData.destination}
              departureDate={savedSearchData.departure_date}
              returnDate={savedSearchData.return_date}
              searchParams={savedSearchData}
              onSelectDates={handleSelectDatesFromGrid}
              onModifySearch={() => {
                setShowFareGrid(false);
                setShowResults(false);
                setTimeout(() => {
                  document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              onBack={handleBackFromFareGrid}
            />
          ) : (
            // One Way - Show simple date fare view with header bar
            <div className="container mx-auto px-4 py-8">
              {/* Header Bar - Same style as FlightResults */}
              <div className="bg-brand-600 text-white rounded-lg shadow-lg p-4 mb-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-start">
                    <Plane className="h-6 w-6" />
                    <span className="font-semibold text-lg">{savedSearchData.origin}</span>
                    <ArrowRight className="h-5 w-5" />
                    <span className="font-semibold text-lg">{savedSearchData.destination}</span>
                    <span className="text-brand-100 text-sm">
                      {savedSearchData.departure_date && format(new Date(savedSearchData.departure_date), 'dd MMM yyyy')}
                    </span>
                    <Badge className="bg-white text-brand-600">One Way</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleBackFromFareGrid}
                      variant="secondary"
                      className="bg-white/20 text-white hover:bg-white/30 font-semibold"
                    >
                      ← Back to Results
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowFareGrid(false);
                        setShowResults(false);
                        setTimeout(() => {
                          document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      variant="secondary"
                      className="bg-white text-brand-600 hover:bg-brand-50 font-semibold"
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Modify
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-center py-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    ±3 Days Fare Comparison
                  </h2>
                  <p className="text-lg text-slate-600 mb-4">
                    <span className="font-semibold">{savedSearchData.origin}</span> → <span className="font-semibold">{savedSearchData.destination}</span>
                  </p>
                  <p className="text-slate-500 mb-6">
                    View fares for dates around {savedSearchData.departure_date ? format(new Date(savedSearchData.departure_date), 'dd MMM yyyy') : 'your selected date'}
                  </p>
                  <div className="text-sm text-slate-500 mb-6">
                    The fare calendar shows prices for flexible dates. For One Way trips, fares are displayed in the search calendar.
                  </div>
                  <Button 
                    onClick={() => {
                      setShowFareGrid(false);
                      setShowResults(false);
                      setTimeout(() => {
                        document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className="bg-brand-600 hover:bg-brand-700"
                  >
                    Modify Search to See All Fares
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Umrah Builder - Full Umrah Package Builder */}
      {showUmrahBuilder && (
        <section className="pb-16 bg-gradient-to-br from-green-50 via-slate-50 to-green-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <div className="container mx-auto px-4">
            <UmrahPackages user={user} />
            <div className="text-center mt-6">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowUmrahBuilder(false);
                  setPreselectedUmrahDeal(null); // Clear preselected deal
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                ← Back to Flight Search
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Hero Section - Hide when loading, showing results, booking, fare grid, umrah builder, manage booking, customer dashboard, or admin */}
      {!isLoading && !showResults && !showBooking && !showFareGrid && !showUmrahBuilder && !showManageBooking && !showCustomerDashboard && !showAdminDashboard && (
        <section 
          id="home" 
          className="relative pb-16 md:pb-24 overflow-hidden"
          style={{ paddingTop: `${headerHeight + 24}px` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-brand-50 to-slate-50 -z-10"></div>
          <div className="container mx-auto px-4">
            <div className="text-center mb-6 md:mb-12 space-y-4">
              <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900">
                <span className="block leading-normal">Find Your Perfect</span>
                <span className="block leading-normal text-brand-600 mt-2 pb-4">
                  Flight Deal
                </span>
              </h1>
              <p className="text-xs sm:text-sm md:text-lg text-slate-600 max-w-2xl mx-auto">
                Compare prices from 500+ airlines and book your perfect trip
              </p>
            </div>

            {/* Flight Search Component - Pass saved data for modify */}
            <FlightSearch 
              key={savedSearchData ? `search-${savedSearchData.origin}-${savedSearchData.destination}-${savedSearchData.departure_date}` : 'search-default'}
              onSearch={handleSearch} 
              initialData={savedSearchData}
              globalCurrency={globalCurrency}
              onDepartureChange={setGlobalDepartureAirport}
            />

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 md:mt-12 max-w-4xl mx-auto">
              <div className="text-center p-4 md:p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">1M+</div>
                <div className="text-xs sm:text-sm text-blue-700 font-medium">Happy Travelers</div>
              </div>
              <div className="text-center p-4 md:p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Plane className="h-5 w-5 text-white" />
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600">500+</div>
                <div className="text-xs sm:text-sm text-purple-700 font-medium">Airlines</div>
              </div>
              <div className="text-center p-4 md:p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Globe className="h-5 w-5 text-white" />
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-emerald-600">180+</div>
                <div className="text-xs sm:text-sm text-emerald-700 font-medium">Countries</div>
              </div>
              <div className="text-center p-4 md:p-5 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Headphones className="h-5 w-5 text-white" />
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600">24/7</div>
                <div className="text-xs sm:text-sm text-amber-700 font-medium">Support</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Hidden Search Form for Modify - shows when results are visible */}
      {(showResults || isLoading) && (
        <section id="home" className="hidden">
          <FlightSearch 
            onSearch={handleSearch} 
            initialData={savedSearchData}
            globalCurrency={globalCurrency}
            onDepartureChange={setGlobalDepartureAirport}
          />
        </section>
      )}

      {/* Loading State - Full Screen with Modify Button Only */}
      {isLoading && (
        <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-brand-50">
          <div className="container mx-auto px-4">
            <Card className="max-w-lg mx-auto p-8 text-center shadow-lg">
              {/* Search Summary */}
              <div className="mb-6 pb-6 border-b">
                {searchParams?.legs?.length ? (
                  <div className="space-y-1" data-testid="loading-multicity-summary">
                    {searchParams.legs.map((l, i) => (
                      <div key={i} className="flex items-center justify-center gap-3 text-base font-semibold text-slate-800">
                        <span className="text-xs font-bold text-brand-600">S{i + 1}</span>
                        <span>{l.origin}</span><span className="text-brand-600">✈</span><span>{l.destination}</span>
                        <span className="text-sm font-normal text-slate-500">{l.departure_date}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-3 text-lg font-semibold text-slate-800">
                      <span>{searchParams?.origin}</span>
                      <span className="text-brand-600">✈</span>
                      <span>{searchParams?.destination}</span>
                    </div>
                    <div className="text-sm text-slate-500 mt-2">
                      {searchParams?.departure_date} {searchParams?.return_date && `- ${searchParams?.return_date}`}
                    </div>
                  </>
                )}
                {isFlexibleSearch && (
                  <Badge className="mt-2 bg-brand-100 text-brand-700">±3 Days Flexible</Badge>
                )}
              </div>

              {/* Loading Animation */}
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-200 border-t-brand-600"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-brand-600 text-xl">✈</span>
                  </div>
                </div>
                <div>
                  <p className="text-xl font-semibold text-slate-800">Flight is searching...</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {isFlexibleSearch 
                      ? 'Checking multiple date combinations for best prices' 
                      : 'Finding the best flights for you'}
                  </p>
                </div>
              </div>

              {/* Only Modify Button Visible During Loading */}
              <Button 
                onClick={() => {
                  setIsLoading(false);
                  setShowResults(false);
                  setTimeout(() => {
                    document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                variant="outline"
                className="border-brand-600 text-brand-600 hover:bg-brand-50"
              >
                Modify Search
              </Button>
            </Card>
          </div>
        </section>
      )}
      
      {searchError && !isLoading && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <Card className="p-6 bg-red-50 border-red-200">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Search Error</h3>
              <p className="text-brand-700">{searchError.message || 'Unable to search flights. Please try again.'}</p>
              <Button 
                onClick={() => {
                  setShowResults(false);
                  setSearchError(null);
                }}
                className="mt-4 bg-brand-600 hover:bg-brand-700"
              >
                Try Again
              </Button>
            </Card>
          </div>
        </section>
      )}
      
      {showResults && !isLoading && !showFareGrid && !duffelFlight && searchParams?.tripType === 'multi-city' && (
        <section id="search-results" className="pb-8 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <MultiCityResults
            legs={searchParams.legs || []}
            itineraries={searchResults}
            passengers={{ adults: searchParams.adults || 1, youth: searchParams.youth || 0, children: searchParams.children || 0, infants: searchParams.infants || 0 }}
            onSelectFlight={handleSelectFlight}
            isRevalidating={isRevalidating}
            note={multiCityNote}
            onModifySearch={() => {
              setShowResults(false);
              setShowFareGrid(false);
              setTimeout(() => {
                document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
          />
        </section>
      )}

      {showResults && !isLoading && !showFareGrid && !duffelFlight && searchParams?.tripType !== 'multi-city' && (
        <section id="search-results" className="pb-8 bg-slate-50 min-h-screen" style={{ paddingTop: `${headerHeight + 24}px` }}>
          <FlightResults 
            flights={searchResults}
            pinnedFlights={pinnedFlights}
            pinnedAirlineName={pinnedAirlineName}
            pinnedAirlineCode={pinnedAirlineCode}
            isFlexible={isFlexibleSearch}
            onSelectFlight={handleSelectFlight}
            searchParams={searchParams}
            isLoading={isLoading}
            isRevalidating={isRevalidating}
            revalidationError={revalidationError}
            onModifySearch={() => {
              setShowResults(false);
              setShowFareGrid(false);
              setTimeout(() => {
                document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            onShowFareGrid={handleShowFareGrid}
            onInlineSearch={(searchData) => {
              // Handle inline search - trigger new search without leaving results page
              handleSearch(searchData);
            }}
            onClearRevalidationError={() => setRevalidationError(null)}
          />
        </section>
      )}

      {/* Hide all landing page sections when Umrah Builder, Results, Booking, Loading, Manage Booking, Customer Dashboard, or Admin is active */}
      {!showUmrahBuilder && !showResults && !showBooking && !isLoading && !showManageBooking && !showCustomerDashboard && !showAdminDashboard && (
      <>
      {/* Features Section */}
      {/* Why Choose Flight380 Section - Hidden on Mobile */}
      <section className="hidden md:block py-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-yellow-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          {/* Decorative shapes */}
          <div className="absolute top-20 right-20 w-20 h-20 border-4 border-white/20 rounded-full"></div>
          <div className="absolute bottom-32 left-32 w-16 h-16 border-4 border-white/10 rounded-lg rotate-45"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-white/20 backdrop-blur-sm text-white px-6 py-2 text-sm font-bold mb-4 shadow-lg border border-white/30">
              Why Us
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Why Choose <span className="text-yellow-300">Flight380?</span>
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              We make flight booking simple, secure, and rewarding
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              const colorSchemes = [
                { bg: 'from-rose-500 to-pink-600', iconBg: 'bg-rose-500', accent: 'text-rose-500', glow: 'shadow-rose-500/30' },
                { bg: 'from-violet-500 to-purple-600', iconBg: 'bg-violet-500', accent: 'text-violet-500', glow: 'shadow-violet-500/30' },
                { bg: 'from-emerald-500 to-teal-600', iconBg: 'bg-emerald-500', accent: 'text-emerald-500', glow: 'shadow-emerald-500/30' },
                { bg: 'from-amber-500 to-orange-600', iconBg: 'bg-amber-500', accent: 'text-amber-500', glow: 'shadow-amber-500/30' },
                { bg: 'from-blue-500 to-cyan-600', iconBg: 'bg-blue-500', accent: 'text-blue-500', glow: 'shadow-blue-500/30' },
                { bg: 'from-fuchsia-500 to-pink-600', iconBg: 'bg-fuchsia-500', accent: 'text-fuchsia-500', glow: 'shadow-fuchsia-500/30' },
              ];
              const scheme = colorSchemes[index % colorSchemes.length];
              return (
                <div key={feature.id} className="group relative bg-white rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-3">
                  {/* Top gradient bar */}
                  <div className={`h-2 bg-gradient-to-r ${scheme.bg}`}></div>
                  
                  {/* Decorative corner gradient */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl ${scheme.bg} opacity-10 rounded-bl-full`}></div>
                  
                  <div className="p-8 relative">
                    {/* Icon */}
                    <div className={`w-20 h-20 bg-gradient-to-br ${scheme.bg} rounded-2xl flex items-center justify-center mb-6 shadow-xl ${scheme.glow} group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                    
                    {/* Description */}
                    <p className="text-slate-600 leading-relaxed mb-4">{feature.description}</p>
                    
                    {/* Learn more link */}
                    <a 
                      href="#contact" 
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className={`flex items-center gap-2 ${scheme.accent} font-semibold text-sm group-hover:gap-3 transition-all duration-300 cursor-pointer hover:underline`}
                    >
                      <span>Learn more</span>
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                  
                  {/* Bottom accent line */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${scheme.bg} opacity-50`}></div>
                </div>
              );
            })}
          </div>
          
          {/* Stats row */}
          <div className="flex flex-wrap justify-center gap-8 mt-16">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-4 rounded-2xl">
              <div className="w-12 h-12 bg-yellow-400 rounded-xl flex items-center justify-center">
                <Star className="h-6 w-6 text-yellow-900 fill-yellow-900" />
              </div>
              <div className="text-white">
                <div className="font-black text-2xl">4.9/5</div>
                <div className="text-sm text-white/70">Customer Rating</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-4 rounded-2xl">
              <div className="w-12 h-12 bg-emerald-400 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-emerald-900" />
              </div>
              <div className="text-white">
                <div className="font-black text-2xl">1M+</div>
                <div className="text-sm text-white/70">Happy Customers</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-4 rounded-2xl">
              <div className="w-12 h-12 bg-blue-400 rounded-xl flex items-center justify-center">
                <Plane className="h-6 w-6 text-blue-900" />
              </div>
              <div className="text-white">
                <div className="font-black text-2xl">500+</div>
                <div className="text-sm text-white/70">Partner Airlines</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Holidays Destinations */}
      <section id="destinations" className="py-20 bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-20 w-72 h-72 bg-purple-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-cyan-200/40 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-pink-200/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white px-6 py-2 text-sm font-bold mb-4 shadow-lg">
              Explore
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">Popular Holidays Destinations</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Explore the world's most amazing destinations at unbeatable prices
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {popularDestinations.map((destination, index) => {
              const cardStyles = [
                { gradient: 'from-rose-500 to-pink-600', border: 'hover:border-rose-400', ring: 'ring-rose-400' },
                { gradient: 'from-violet-500 to-purple-600', border: 'hover:border-violet-400', ring: 'ring-violet-400' },
                { gradient: 'from-slate-700 to-slate-900', border: 'hover:border-slate-500', ring: 'ring-slate-400' },
                { gradient: 'from-amber-500 to-orange-600', border: 'hover:border-amber-400', ring: 'ring-amber-400' },
                { gradient: 'from-yellow-500 to-amber-600', border: 'hover:border-yellow-400', ring: 'ring-yellow-400' },
                { gradient: 'from-teal-500 to-cyan-600', border: 'hover:border-teal-400', ring: 'ring-teal-400' },
              ];
              const style = cardStyles[index % cardStyles.length];
              // Hide cards beyond first 3 on mobile (show all on md+)
              const mobileHiddenClass = index >= 3 ? 'hidden md:block' : '';
              return (
                <div 
                  key={destination.id} 
                  className={`${mobileHiddenClass} group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-3 border-2 border-transparent ${style.border}`}
                  onClick={() => {
                    setSelectedHolidayDestination(destination);
                    setShowHolidayPackageBuilder(true);
                  }}
                >
                  {/* Image Section */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={destination.imageUrl} 
                      alt={destination.city}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    
                    {/* Price Badge */}
                    <div className={`absolute top-4 right-4 bg-gradient-to-r ${style.gradient} text-white font-bold px-4 py-2 rounded-full shadow-lg text-sm`}>
                      From <Money gbp={destination.price} />
                    </div>
                    
                    {/* Destination Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-2xl font-bold text-white mb-1 drop-shadow-lg">{destination.city}</h3>
                      <p className="text-white/90 text-sm font-medium">{destination.country}</p>
                    </div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-5 bg-gradient-to-b from-white to-slate-50">
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">{destination.description}</p>
                    
                    {/* Package Tiers Preview */}
                    <div className="flex items-center gap-2 mb-4 text-xs">
                      <span className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-medium">
                        Economy
                      </span>
                      <span className="flex items-center gap-1 bg-slate-200 text-slate-700 px-2 py-1 rounded-full font-medium">
                        Standard
                      </span>
                      <span className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                        Premium
                      </span>
                    </div>
                    
                    {/* CTA Button */}
                    <button className={`w-full flex items-center justify-between px-5 py-3 rounded-xl bg-gradient-to-r ${style.gradient} text-white font-semibold hover:opacity-90 transition-all duration-300 shadow-md group/btn`}>
                      <span>View Packages</span>
                      <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* View All Button - Show on mobile only since we hide some destinations */}
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="md:hidden bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-600 hover:via-blue-600 hover:to-purple-600 text-white font-bold text-lg px-10 shadow-xl"
              onClick={() => setShowExploreDestinations(true)}
            >
              View All Destinations →
            </Button>
            <Button 
              size="lg" 
              className="hidden md:inline-flex bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-600 hover:via-blue-600 hover:to-purple-600 text-white font-bold text-lg px-10 shadow-xl"
              onClick={() => setShowExploreDestinations(true)}
            >
              Explore All Destinations →
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials - Hidden on Mobile */}
      <section className="hidden md:block py-20 bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-pink-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          {/* Floating quote marks */}
          <div className="absolute top-20 left-20 text-white/10 text-[200px] font-serif leading-none">"</div>
          <div className="absolute bottom-20 right-20 text-white/10 text-[200px] font-serif leading-none rotate-180">"</div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-white/20 backdrop-blur-sm text-white px-6 py-2 text-sm font-bold mb-4 shadow-lg border border-white/30">
              Testimonials
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Trusted by <span className="text-yellow-300">Travelers</span>
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              See what our customers say about their experience
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => {
              const cardStyles = [
                { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-50', accent: 'text-rose-600', ring: 'ring-rose-400' },
                { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', accent: 'text-emerald-600', ring: 'ring-emerald-400' },
                { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', accent: 'text-amber-600', ring: 'ring-amber-400' },
              ];
              const style = cardStyles[index % cardStyles.length];
              return (
                <div key={testimonial.id} className="group relative bg-white rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-3">
                  {/* Top gradient bar */}
                  <div className={`h-2 bg-gradient-to-r ${style.gradient}`}></div>
                  
                  {/* Quote icon */}
                  <div className="absolute top-6 right-6">
                    <div className={`w-12 h-12 bg-gradient-to-br ${style.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    {/* Star Rating */}
                    <div className="flex gap-1 mb-5">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400 drop-shadow-sm" />
                      ))}
                    </div>
                    
                    {/* Testimonial Content */}
                    <p className="text-slate-700 text-lg leading-relaxed mb-6 font-medium">"{testimonial.content}"</p>
                    
                    {/* Divider */}
                    <div className={`h-px bg-gradient-to-r ${style.gradient} opacity-30 mb-6`}></div>
                    
                    {/* Author Info */}
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${style.gradient} rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg ring-4 ring-white`}>
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-lg">{testimonial.name}</h4>
                        <p className={`text-sm font-medium ${style.accent}`}>{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom accent */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${style.gradient} opacity-50`}></div>
                </div>
              );
            })}
          </div>
          
          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-8 mt-12">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                <Star className="h-5 w-5 text-yellow-900 fill-yellow-900" />
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">4.9/5 Rating</div>
                <div className="text-sm text-white/70">Based on 10,000+ reviews</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <div className="w-10 h-10 bg-emerald-400 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-900" />
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">Verified Reviews</div>
                <div className="text-sm text-white/70">100% authentic feedback</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flight Deals Section */}
      <section id="flight-deals" className="py-20 bg-gradient-to-br from-blue-600 via-brand-600 to-orange-500 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-yellow-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-white/20 backdrop-blur-sm text-white px-6 py-2 text-sm font-bold mb-4 shadow-lg border border-white/30">
              Hot Flight Deals
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Exclusive <span className="text-yellow-300">Flight Offers</span>
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Discover amazing flight deals to the world's most popular destinations. Book now and save big!
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(() => {
              // 8 Flight Deals to specific destinations
              const flightDeals = [
                { id: 1, city: 'Islamabad', country: 'Pakistan', code: 'ISB', price: 389, image: '🇵🇰', gradient: 'from-emerald-500 to-teal-600', discount: 20 },
                { id: 2, city: 'Dubai', country: 'United Arab Emirates', code: 'DXB', price: 299, image: '🇦🇪', gradient: 'from-amber-500 to-orange-600', discount: 25 },
                { id: 3, city: 'Delhi', country: 'India', code: 'DEL', price: 359, image: '🇮🇳', gradient: 'from-orange-500 to-red-600', discount: 15 },
                { id: 4, city: 'Istanbul', country: 'Turkey', code: 'IST', price: 199, image: '🇹🇷', gradient: 'from-red-500 to-rose-600', discount: 30 },
                { id: 5, city: 'Jeddah', country: 'Saudi Arabia', code: 'JED', price: 329, image: '🇸🇦', gradient: 'from-green-500 to-emerald-600', discount: 18 },
                { id: 6, city: 'Dhaka', country: 'Bangladesh', code: 'DAC', price: 419, image: '🇧🇩', gradient: 'from-teal-500 to-cyan-600', discount: 12 },
                { id: 7, city: 'Kuala Lumpur', country: 'Malaysia', code: 'KUL', price: 449, image: '🇲🇾', gradient: 'from-blue-500 to-indigo-600', discount: 22 },
                { id: 8, city: 'Doha', country: 'Qatar', code: 'DOH', price: 279, image: '🇶🇦', gradient: 'from-purple-500 to-violet-600', discount: 28 },
              ];
              
              const today = new Date();
              
              return flightDeals.map((deal) => {
                // Generate dynamic departure date (1-4 weeks from now)
                const departDate = new Date(today);
                departDate.setDate(today.getDate() + 7 + (deal.id * 3));
                
                // Calculate original price before discount
                const originalPrice = Math.round(deal.price / (1 - deal.discount / 100));
                // Hide cards beyond first 3 on mobile
                const mobileHiddenClass = deal.id > 3 ? 'hidden md:block' : '';
                
                return (
                  <div key={deal.id} className={`${mobileHiddenClass} group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2`}>
                    {/* Discount Badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <Badge className={`bg-gradient-to-r ${deal.gradient} text-white font-bold px-3 py-1 text-sm shadow-lg`}>
                        {deal.discount}% OFF
                      </Badge>
                    </div>
                    
                    {/* Header with gradient and flag */}
                    <div className={`bg-gradient-to-r ${deal.gradient} p-5 text-white relative`}>
                      <div className="absolute top-3 left-4 text-4xl opacity-30">{deal.image}</div>
                      <div className="relative z-10">
                        <div className="text-xs opacity-80 font-medium">From London</div>
                        <div className="font-bold text-2xl mt-1">{deal.city}</div>
                        <div className="text-sm opacity-90 flex items-center gap-1">
                          <span>{deal.country}</span>
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full ml-1">{deal.code}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-5">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500 flex items-center gap-1">
                            <Plane className="w-4 h-4" />
                            Departs
                          </span>
                          <span className="font-semibold text-slate-700">
                            {departDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          <span>Return flights included</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          <span>23kg baggage allowance</span>
                        </div>
                      </div>
                      
                      <div className="mt-5 pt-4 border-t">
                        <div className="flex items-end justify-between">
                          <div>
                            <div className="text-xs text-slate-500">From</div>
                            <div className="flex items-baseline gap-2">
                              <span className={`text-3xl font-bold bg-gradient-to-r ${deal.gradient} bg-clip-text text-transparent`}>
                                <Money gbp={deal.price} />
                              </span>
                              <span className="text-sm text-slate-400 line-through"><Money gbp={originalPrice} /></span>
                            </div>
                            <div className="text-xs text-emerald-600 font-semibold">Save <Money gbp={originalPrice - deal.price} /></div>
                            <div className="text-xs text-slate-500">per person</div>
                          </div>
                          <Button 
                            size="sm" 
                            type="button"
                            className={`bg-gradient-to-r ${deal.gradient} hover:opacity-90 text-white font-semibold shadow-md px-5`}
                            onClick={(e) => {
                              e.preventDefault();
                              // Pre-fill search and trigger search for this destination
                              const departDate = new Date();
                              departDate.setDate(departDate.getDate() + 7 + (deal.id * 3));
                              const returnDate = new Date(departDate);
                              returnDate.setDate(returnDate.getDate() + 7);
                              
                              // Trigger search with this destination
                              handleSearch({
                                origin: 'LON',
                                destination: deal.code,
                                departure_date: departDate.toISOString().split('T')[0],
                                return_date: returnDate.toISOString().split('T')[0],
                                adults: 1,
                                children: 0,
                                infants: 0,
                                youth: 0,
                                travel_class: 'economy',
                                direct_flights: false,
                                tripType: 'return'
                              });
                            }}
                          >
                            Book Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          
          <div className="text-center mt-10">
            <Button 
              size="lg" 
              className="bg-white text-brand-600 hover:bg-yellow-50 font-bold text-lg px-10 shadow-xl border-2 border-white/50"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              View All Flight Deals →
            </Button>
          </div>
        </div>
      </section>

      {/* Umrah Deals Section */}
      <section id="deals" className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-brand-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-900 px-4 py-1.5 text-sm font-bold mb-4">
              Special Umrah Packages
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Exclusive <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent">Umrah Deals</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Book your spiritual journey with our handpicked Umrah packages. Updated daily with the best prices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(() => {
              // Generate 8 Umrah deals with different dates
              const generateUmrahDeals = () => {
                const deals = [];
                const today = new Date();
                const cities = ['London', 'Manchester', 'Birmingham', 'Edinburgh'];
                const durations = [10, 14, 21, 7];
                const basePrice = [899, 1099, 1299, 799];
                const hotelStars = [3, 4, 5, 3];
                const gradients = [
                  'from-brand-500 to-orange-500',
                  'from-purple-500 to-pink-500',
                  'from-blue-500 to-cyan-500',
                  'from-emerald-500 to-teal-500',
                  'from-amber-500 to-yellow-500',
                  'from-rose-500 to-red-500',
                  'from-indigo-500 to-purple-500',
                  'from-cyan-500 to-blue-500',
                ];
                
                for (let i = 0; i < 8; i++) {
                  const departDate = new Date(today);
                  departDate.setDate(today.getDate() + 7 + (i * 7)); // Weekly departures
                  const returnDate = new Date(departDate);
                  returnDate.setDate(departDate.getDate() + durations[i % 4]);
                  
                  // Daily price variation based on day of year
                  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
                  const priceVariation = ((dayOfYear + i) % 100) - 50;
                  
                  deals.push({
                    id: i + 1,
                    city: cities[i % 4],
                    duration: durations[i % 4],
                    departDate: departDate,
                    returnDate: returnDate,
                    price: basePrice[i % 4] + priceVariation,
                    hotelStars: hotelStars[i % 4],
                    gradient: gradients[i],
                    discount: i < 2 ? 15 : i < 4 ? 10 : 0,
                  });
                }
                return deals;
              };
              
              const umrahDeals = generateUmrahDeals();
              
              return umrahDeals.map((deal, index) => (
                <div key={deal.id} className="group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  {/* Discount Badge */}
                  {deal.discount > 0 && (
                    <div className="absolute top-4 left-4 z-10">
                      <Badge className={`bg-gradient-to-r ${deal.gradient} text-white font-bold px-3 py-1 text-sm shadow-lg`}>
                        {deal.discount}% OFF
                      </Badge>
                    </div>
                  )}
                  
                  {/* Header with gradient */}
                  <div className={`bg-gradient-to-r ${deal.gradient} p-4 text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs opacity-80">From {deal.city}</div>
                        <div className="font-bold text-lg">Makkah & Madinah</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs opacity-80">{deal.duration} Days</div>
                        <div className="flex items-center gap-0.5">
                          {[...Array(deal.hotelStars)].map((_, i) => (
                            <Star key={i} className="h-3 w-3 fill-yellow-300 text-yellow-300" />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Departure</span>
                        <span className="font-semibold text-slate-700">
                          {deal.departDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Return</span>
                        <span className="font-semibold text-slate-700">
                          {deal.returnDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <span>Flights + Hotels + Visa</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <span>Airport Transfers</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-end justify-between">
                        <div>
                          <div className="text-xs text-slate-500">From</div>
                          <div className={`text-2xl font-bold bg-gradient-to-r ${deal.gradient} bg-clip-text text-transparent`}>
                            <Money gbp={deal.price} />
                          </div>
                          <div className="text-xs text-slate-500">per person</div>
                        </div>
                        <Button 
                          size="sm" 
                          className={`bg-gradient-to-r ${deal.gradient} hover:opacity-90 text-white font-semibold shadow-md`}
                          onClick={() => {
                            // Set the preselected deal and open Umrah Builder
                            setPreselectedUmrahDeal({
                              city: deal.city,
                              departDate: deal.departDate,
                              returnDate: deal.returnDate,
                              duration: deal.duration,
                              hotelStars: deal.hotelStars,
                              price: deal.price,
                              discount: deal.discount
                            });
                            setShowUmrahBuilder(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Book Now
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
          
          <div className="text-center mt-10">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-900 hover:from-amber-500 hover:to-yellow-500 font-bold text-lg px-10 shadow-xl"
              onClick={() => handleSearch({ tripType: 'umrah' })}
            >
              View All Umrah Packages →
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Multi-color gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-fuchsia-500 via-pink-500 to-orange-500"></div>
        
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)'}}></div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-40 h-40 bg-yellow-400/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-cyan-400/30 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
          <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-pink-400/20 rounded-full blur-3xl"></div>
          {/* Floating shapes */}
          <div className="absolute top-20 right-20 w-16 h-16 border-4 border-white/30 rounded-full"></div>
          <div className="absolute bottom-20 left-20 w-12 h-12 border-4 border-white/20 rounded-lg rotate-45"></div>
          <div className="absolute top-1/2 right-10 w-8 h-8 bg-yellow-400/40 rounded-full"></div>
          <div className="absolute bottom-1/3 left-1/3 w-6 h-6 bg-cyan-400/40 rounded-full"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg border border-white/30">
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-400"></span>
              </span>
              Special Offers Available
            </div>
            
            {/* Heading */}
            <h2 className="text-4xl md:text-6xl font-black text-white leading-tight">
              Ready to Start Your
              <span className="block mt-2 bg-gradient-to-r from-yellow-300 via-amber-300 to-orange-300 bg-clip-text text-transparent">
                Journey?
              </span>
            </h2>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Join over <span className="font-bold text-yellow-300">1 million</span> happy travelers and discover your next adventure with exclusive deals and offers.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center pt-4">
              <Button size="lg" className="bg-white text-fuchsia-600 hover:bg-yellow-50 font-bold text-lg px-10 py-6 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1 rounded-xl">
                <Plane className="w-5 h-5 mr-2" />
                Search Flights Now
              </Button>
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 hover:from-yellow-300 hover:to-orange-300 font-bold text-lg px-10 py-6 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1 border-0 rounded-xl">
                <Gift className="w-5 h-5 mr-2" />
                View All Deals
              </Button>
            </div>
            
            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-4 pt-8">
              <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm px-5 py-3 rounded-2xl border border-white/20">
                <div className="w-10 h-10 bg-emerald-400 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-900" />
                </div>
                <span className="text-white font-semibold">No Hidden Fees</span>
              </div>
              <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm px-5 py-3 rounded-2xl border border-white/20">
                <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center">
                  <Shield className="h-5 w-5 text-yellow-900" />
                </div>
                <span className="text-white font-semibold">Best Price Guarantee</span>
              </div>
              <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm px-5 py-3 rounded-2xl border border-white/20">
                <div className="w-10 h-10 bg-cyan-400 rounded-xl flex items-center justify-center">
                  <Headphones className="h-5 w-5 text-cyan-900" />
                </div>
                <span className="text-white font-semibold">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Hidden on Mobile */}
      <section id="about" className="hidden md:block py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-amber-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-rose-200/40 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 text-sm font-bold mb-4 shadow-lg">
                About Us
              </Badge>
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600 bg-clip-text text-transparent">Your Trusted Travel Partner</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Flight380 has been helping travelers find the best flight deals since 2015. We're passionate about making air travel accessible and affordable for everyone.
              </p>
            </div>

            {/* Company Story */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-4">Our Story</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Founded in London, Flight380 started with a simple mission: to make flight booking easier and more affordable. Our name comes from the iconic Airbus A380, symbolizing our commitment to connecting people across the globe.
                </p>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Today, we partner with over 500 airlines worldwide, offering our customers access to millions of flight options at competitive prices. Our advanced search technology compares fares across multiple providers to ensure you always get the best deal.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We believe everyone deserves to explore the world. That's why we're committed to transparency, with no hidden fees and clear pricing every step of the way.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {/* Stat Card 1 - Happy Customers */}
                <div className="group bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl p-6 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Users className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-4xl font-black text-white mb-1">1M+</div>
                  <div className="text-rose-100 font-medium">Happy Customers</div>
                </div>
                
                {/* Stat Card 2 - Partner Airlines */}
                <div className="group bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Plane className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-4xl font-black text-white mb-1">500+</div>
                  <div className="text-violet-100 font-medium">Partner Airlines</div>
                </div>
                
                {/* Stat Card 3 - Countries Served */}
                <div className="group bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Globe className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-4xl font-black text-white mb-1">190+</div>
                  <div className="text-blue-100 font-medium">Countries Served</div>
                </div>
                
                {/* Stat Card 4 - Years Experience */}
                <div className="group bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Award className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-4xl font-black text-white mb-1">10+</div>
                  <div className="text-amber-100 font-medium">Years Experience</div>
                </div>
              </div>
            </div>

            {/* Our Values */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-rose-600 bg-clip-text text-transparent mb-8 text-center">Our Values</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Value 1 - Customer First */}
                <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/50">
                  <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg group-hover:scale-110 transition-transform">
                    <Heart className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Customer First</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Your satisfaction is our priority. We're here to help you find the perfect flight at the best price.
                  </p>
                </div>
                
                {/* Value 2 - Trust & Transparency */}
                <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/50">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg group-hover:scale-110 transition-transform">
                    <Shield className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Trust & Transparency</h4>
                  <p className="text-slate-600 leading-relaxed">
                    No hidden fees, no surprises. What you see is what you pay. We believe in honest, upfront pricing.
                  </p>
                </div>
                
                {/* Value 3 - Innovation */}
                <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/50">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg group-hover:scale-110 transition-transform">
                    <Zap className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-3">Innovation</h4>
                  <p className="text-slate-600 leading-relaxed">
                    We continuously improve our platform to provide you with the fastest, most efficient booking experience.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - Hidden on Mobile */}
      <section id="contact" className="hidden md:block py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-brand-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <Badge className="bg-gradient-to-r from-brand-500 to-purple-500 text-white px-6 py-2 text-sm font-bold mb-4 shadow-lg">
                Contact Us
              </Badge>
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-brand-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">Get in Touch</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Have questions about your booking or need assistance? Our friendly team is here to help you 24/7.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-brand-600 to-purple-600 bg-clip-text text-transparent mb-6">Contact Information</h3>
                
                <div className="space-y-5">
                  {/* Head Office Card */}
                  <Card className="p-5 hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:-translate-y-1 overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-500 to-orange-500"></div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <MapPin className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1 text-lg">Head Office</h4>
                        <p className="text-slate-600">
                          Flight380 Ltd<br />
                          277 Dunstable Road<br />
                          Luton, Bedfordshire<br />
                          LU4 8BS
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Phone Card */}
                  <Card className="p-5 hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:-translate-y-1 overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Phone className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1 text-lg">Phone</h4>
                        <p className="text-slate-600">
                          <a href="tel:01908220000" className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent hover:underline">01908 220000 / 01582 808050</a><br />
                          <span className="inline-flex items-center gap-1 mt-1 text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Available 24/7
                          </span>
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Email Card */}
                  <Card className="p-5 hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:-translate-y-1 overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Mail className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-2 text-lg">Email</h4>
                        <div className="space-y-1">
                          <p className="text-slate-600 flex items-center gap-2">
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">General</span>
                            info@flight380.co.uk
                          </p>
                          <p className="text-slate-600 flex items-center gap-2">
                            <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded font-medium">Bookings</span>
                            bookings@flight380.co.uk
                          </p>
                          <p className="text-slate-600 flex items-center gap-2">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">Support</span>
                            support@flight380.co.uk
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* WhatsApp Card */}
                  <Card className="p-5 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-lg hover:-translate-y-1 overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <svg viewBox="0 0 24 24" className="h-7 w-7 text-white fill-current">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-green-800 mb-1 text-lg">WhatsApp Support</h4>
                        <p className="text-green-700">
                          <a href="https://wa.me/447404386262" target="_blank" rel="noopener noreferrer" className="text-xl font-bold hover:underline">+44 7404 386262</a><br />
                          <span className="inline-flex items-center gap-1 mt-1 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                            <Clock className="w-3 h-3" />
                            08:00 AM - 11:59 PM (GMT)
                          </span>
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Customer Service Hours Card */}
                  <Card className="p-5 hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:-translate-y-1 overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-yellow-500"></div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Clock className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-2 text-lg">Customer Service Hours</h4>
                        <div className="space-y-1">
                          <p className="text-slate-600 flex items-center gap-2">
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">Phone</span>
                            24 hours, 7 days a week
                          </p>
                          <p className="text-slate-600 flex items-center gap-2">
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-medium">Email</span>
                            Response within 24 hours
                          </p>
                          <p className="text-slate-600 flex items-center gap-2">
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">Live Chat</span>
                            8:00 AM - 10:00 PM (GMT)
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-6">Send Us a Message</h3>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-0">
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </>
      )}

      <Footer />
    </div>
  );
}

export default App;